import { z } from "zod";
import { CONFLICT_MSG } from "./concurrent.js";
import { PLAN_CATALOG } from "./billing.js";
import { fillProduct, type Product } from "./product.js";
import { DEFAULT_LABELS, fillLabels, type Labels } from "./vocabulary.js";

// ---------------------------------------------------------------------------
// 工程管理（有料オプション。#100、docs/39。射場さん 2026-09-30 の決定）
//   - 案件（project_groups）に工程表を1つ。行は 工程・見出し・メモ・節目。前が終わったら後ろが始まる「つなぎ」
//   - 休みの日（既定：日曜・祝日）は日数に数えない。日数＝稼働日の数
//   - 日付は 'YYYY-MM-DD' の文字列で持つ（時差で1日ずれないように。Date を使わず、1970-01-01 からの通し番号で数える）
//   - ここの関数は画面と API が同じものを使う（計算は1か所）。終わりの日・押し出しは API が normalizeSchedule をかけて保存する
// ---------------------------------------------------------------------------

/** 上限（docs/39 §3.8・§4） */
export const SCHEDULE_LIMITS = {
  rows: 500,
  links: 1000,
  name: 200,
  memo: 500,
  note: 200,
  assigneeText: 60,
  workDays: 999,
  /** 中止の日（1工程表に。期間の中止は1日1つ） */
  stops: 366,
  /** 中止の期間（から〜まで、両端を含む） */
  stopSpan: 31,
  stopNote: 60,
  /** 工程表の雛形（会社に。DB も数える） */
  templates: 30,
  templateName: 60,
  /** 会社の休み（期間の数・1つの期間の長さ・名前） */
  companyOffDays: 100,
  offDaySpan: 31,
  offDayName: 40,
  /** 人数（工程だけ。#102） */
  crewMax: 99,
  /** {task}の日ごとの休み・作業（#293。1工程表に） */
  rowDays: 3000,
  /** {siteShort}の作業日（#296。中止と同じ 366） */
  siteWorkDays: 366,
  /** 日のメモ（#299。1工程表に・1つの文の字数） */
  dayNotes: 500,
  dayNoteText: 100,
  /** 全体の工程表（#102）：期間は 400日まで・工程表は 300 まで */
  overviewDays: 400,
  overviewSchedules: 300,
} as const;

/** 工程管理の料金（月額・税抜）。料金表（billing.ts の PLAN_CATALOG）に1か所。営業力（スケジュール管理）も同じ額（#142 段5） */
export const SCHEDULE_PRICE = PLAN_CATALOG.genba.options.schedule?.price ?? 0;
const yen = (n: number) => n.toLocaleString("ja-JP");

/**
 * 雨の言い回しを出すか（#142 段5、docs/54 No.125〜128）：現場力だけ。
 * 営業力は「中止」だけで、中止のわけ（雨／ほか）の選択・「雨でも作業する」の印・列・メニュー・CSV の列を出さない（雨を前に出さない）。
 * 文の違い（「雨で中止」⇄「中止」など）は product.ts の words（{pw:stopRain} ほか）。ここは出すか出さないかだけ
 */
export const scheduleShowsRain = (product: Product): boolean => product === "genba";

/**
 * 決まり文を会社の言葉（{schedule}・{task}…）と製品の言い回し（{pw:…}）で埋める関数（#142 段5）。
 * 画面の t() を通らない所（API の紙・Excel・CSV・ファイル名、packages/paper）が使う。省けば現場力の既定の言葉
 */
export const scheduleFill =
  (labels: Labels = DEFAULT_LABELS, product: Product = "genba") =>
  (text: string): string =>
    fillProduct(fillLabels(text, labels), product);

/**
 * 画面に出す文（1か所）。{schedule}・{task}・{contractor}・{siteShort}・{project} は会社の言葉、{pw:…} は製品の言い回し（#142 段5）。
 * 画面は t()（apps/web/src/pages/schedule/msg.ts が通す）、API の紙・Excel・CSV は fillLabels と fillProduct で埋める
 */
export const SCHEDULE_MSG = {
  optionOff: `{schedule}はオプション（月額 ${yen(SCHEDULE_PRICE)}円・税抜）です`,
  optionName: "{pw:scheduleOption}",
  exists: "{schedule}は作成済です",
  notFound: "{schedule}が見つかりません",
  conflict: CONFLICT_MSG.auto,
  cycle: "循環するつなぎは設定できません",
  tooManyRows: `行は${SCHEDULE_LIMITS.rows}行までです`,
  tooManyLinks: `つなぎは${SCHEDULE_LIMITS.links}本までです`,
  rowForeign: "他の{schedule}の行は指定できません",
  rowDuplicate: "同じ行が2つあります",
  linkInvalid: "つなぎは{task}と節目の間のみ設定できます",
  assigneeNotFound: "担当が見つかりません",
  offWeekdaysAll: "休日の曜日は6つまでです",
  nameTooLong: `{task}名は${SCHEDULE_LIMITS.name}字までです`,
  memoTooLong: `メモは${SCHEDULE_LIMITS.memo}字までです`,
  noteTooLong: `説明は${SCHEDULE_LIMITS.note}字までです`,
  assigneeTooLong: `担当は${SCHEDULE_LIMITS.assigneeText}字までです`,
  deleted: (name: string) => `「${name || "（名前なし）"}」を削除しました`,
  headingDeleteTitle: (name: string) => `見出し「${name || "（名前なし）"}」を削除`,
  headingOnly: "見出しのみ削除（行は残す）",
  headingAll: (n: number) => `見出しと ${n}行を削除`,
  scheduleDeleteTitle: "{schedule}削除",
  scheduleDeleteBody: "{schedule}のすべての行とつなぎを削除します。元に戻せません。",
  scheduleDeleted: "{schedule}を削除しました",
  saving: "保存中…",
  saved: "保存しました",
  saveFailed: "保存できませんでした",
  linkAdded: (a: string, b: string) => `「${a || "{task}"}」→「${b || "{task}"}」をつなぎました`,
  linkRemoved: "つなぎを解除しました",
  writeAs: (text: string) => `「${text}」で入力`,
  registerVendor: (text: string) => `「${text}」を{contractor}に登録`,
  noAssignee: "担当なし",
  noMatch: "該当する{contractor}・自社の担当者はありません",
  /* ---- 2回目（#101、docs/39 §4） ---- */
  startTitle: "{schedule}の作成方法",
  fromEstimate: "見積から",
  fromEstimateRecommended: "おすすめ",
  fromTemplate: "テンプレートから",
  fromGroup: "前の{project}から複写",
  blank: "白紙から",
  noEstimate: "この{project}に見積がありません",
  noTemplate: "{schedule}のテンプレートがありません",
  noSource: "{schedule}のあるほかの{project}がありません",
  fromEstimateTitle: "見積から作成",
  fromEstimateAddTitle: "見積から追加",
  fromTemplateTitle: "テンプレートから追加",
  fromGroupTitle: "前の{project}から複写",
  chain: "前の{task}完了後に開始",
  headingOn: (subject: string) => `見出し「${subject || "見積"}」付きで追加`,
  notTask: "対象外",
  createFromEstimate: "{schedule}作成",
  addFromEstimate: "{schedule}に追加",
  itemsSummary: (n: number, start: string, end: string) => `${n}{task}・{pw:start} ${start} → 終わり ${end}`,
  noItemPicked: "{task}にする項目を選択してください",
  estimateNotFound: "見積が見つかりません",
  itemNotFound: "見積の項目が見つかりません。見積が修正されたため、再読込が必要です",
  itemDuplicate: "同じ項目が2つあります",
  templateNotFound: "テンプレートが見つかりません",
  sourceNotFound: "複写元の{schedule}が見つかりません",
  added: (n: number) => `${n}行追加しました`,
  /* 中止 */
  /** 雨の中止の名前（バー・見出しの吹き出し・中止のポップ。現場力「雨で中止」・営業力「中止」） */
  stopButton: "{pw:stopRain}",
  /** 操作の列のボタンと窓の題（#298、docs/102 §2.4.2：両製品。中止と{siteShort}の休みを入れる窓） */
  stopOpsButton: "中止・休み",
  stopTitle: "中止・休み",
  /** 窓の主ボタン（#298。前は「中止」＝stopDo は営業力の理由の選択肢に残す） */
  stopSubmit: "登録",
  stopFrom: "から",
  stopTo: "まで",
  stopReasonRain: "雨",
  stopReasonOther: "その他",
  stopDo: "中止",
  stopList: "中止・休みの日",
  stopUndo: "中止解除",
  stopNone: "中止の日はありません",
  stopSpanOver: `中止は${SCHEDULE_LIMITS.stopSpan}日までです`,
  stopOrder: "「まで」は「から」より後の日にしてください",
  stopTooMany: `中止の日は${SCHEDULE_LIMITS.stops}日までです`,
  stopDone: (dates: string, n: number) => (n ? `${dates} を中止にしました。${n}{task}が延びました` : `${dates} を中止にしました`),
  stopRemoved: (dates: string) => `${dates} の中止を解除しました`,
  stopNoteTooLong: `メモは${SCHEDULE_LIMITS.stopNote}字までです`,
  /* 中止を工程ごとに（#105、docs/39 §4.8.1） */
  stopScopeRows: "{task}選択",
  stopScopeRowsSub: "{pw:stopRowsSub}",
  stopScopeAll: "{siteShort}全体",
  stopScopeAllSub: "{pw:stopAllSub}",
  stopTargets: "この日にかかる{task}",
  stopPickAll: "すべて選択",
  stopPickNone: "選択解除",
  stopNoTargets: "この日にかかる{task}はありません",
  stopPickRows: "中止する{task}を選択してください",
  stopRowInvalid: "中止にできるのはこの{schedule}の{task}のみです",
  /** 結果の1行「2工程を止める → 足場解体と「引き渡し」が2日ずれます」 */
  stopPreview: (what: string, names: string[], more: number, days: number, same: boolean) =>
    names.length ? `${what} → ${names.join("と")}${more ? `ほか${more}件` : ""}が${same ? "" : "最大 "}${days}日後ろへ移動します` : what,
  stopPreviewRows: (n: number) => `${n}{task}を中止`,
  stopPreviewAll: (n: number) => (n ? `{siteShort}全体（${n}{task}）を中止` : "{siteShort}全体を中止"),
  stopDoneRows: (dates: string, n: number, moved: number) => `${dates} に ${n}{task}を中止しました。${moved ? `${moved}{task}が延びました` : ""}`.replace(/。$/, ""),
  stopAllRows: "{siteShort}全体",
  stopThisRow: "{task}中止",
  stopRowRemove: (dates: string) => `中止解除（${dates}）`,
  stopRowRemoved: (name: string, dates: string) => `「${name || "{task}"}」の ${dates} の中止を解除しました`,
  /* ---- 8回目（#293、docs/102 §5）：{task}の日ごとの休み・作業と{siteShort}の休み ---- */
  /** 中止の理由「{siteShort}の休み」（予定として入らない日。{siteShort}全体だけ） */
  stopReasonOff: "{siteShort}の休み",
  stopOffRows: "{siteShort}の休みは{siteShort}全体だけです",
  siteOffMark: "休",
  siteOffDone: (md: string, n: number) => (n ? `${md}を{siteShort}の休みにしました。${n}{task}が延びました` : `${md}を{siteShort}の休みにしました`),
  siteOffRemoved: (md: string) => `${md}の{siteShort}の休みを解除しました`,
  siteOffLabel: (md: string) => `${md} {siteShort}の休み`,
  /** 日付の見出しのセル（押すと{siteShort}の休み） */
  siteOffClick: "クリックで{siteShort}の休み",
  siteOffUnclick: "クリックで{siteShort}の休みを解除",
  /** バーの日（行を選んだとき） */
  rowDayClickOff: "クリックで休み",
  rowDayClickWork: "クリックで作業",
  rowDayOffDone: (mdw: string) => `${mdw}を休みにしました`,
  rowDayWorkDone: (mdw: string) => `${mdw}を作業にしました`,
  rowDayOffTip: (mdw: string) => `${mdw} この{task}は休み`,
  rowDayWorkTip: (mdw: string) => `${mdw} この{task}は作業`,
  /** ⋮ の項目と窓 */
  rowDaysItem: "日ごとの休み・作業",
  rowDaysTitle: "日ごとの休み・作業",
  rowDaysWork: "作業",
  rowDaysOff: "休み",
  rowDaysTooMany: `日ごとの休み・作業は1つの{schedule}に${SCHEDULE_LIMITS.rowDays}日までです`,
  rowDayInvalid: "日ごとの休み・作業は{task}のみ設定できます",
  /** 窓の中の押せない日の理由（薄字） */
  rowDayWhyStop: "中止",
  rowDayWhySiteOff: "{siteShort}の休み",
  rowDayWhyHoliday: "祝日",
  rowDayWhyCompany: "会社の休み",
  rowDayWhyWeekday: "休日",
  rowDayWhySiteWork: "{siteShort}の作業日",
  /* ---- #296（docs/102 §5.8・§5.9・§2.4.1）：{siteShort}の作業日・表の列・表の幅 ---- */
  siteWorkDone: (md: string, n: number) => (n ? `${md}を{siteShort}の作業日にしました。${n}{task}が縮みました` : `${md}を{siteShort}の作業日にしました`),
  siteWorkRemoved: (md: string) => `${md}の{siteShort}の作業日を解除しました`,
  siteWorkLabel: (md: string) => `${md} {siteShort}の作業日`,
  /** 日付の見出しのセル（休みの日を押すと{siteShort}の作業日） */
  siteWorkClick: "クリックで{siteShort}の作業日",
  siteWorkUnclick: "クリックで{siteShort}の作業日を解除",
  /** ⋮「休日：日・祝」の窓の節 */
  workDaysSection: "作業日にした休日",
  workDaysNone: "なし",
  workDaysAdd: "追加",
  workDaysPick: "休日の日付",
  workDaysNotOff: "休日でない日は作業日にできません",
  workDaysRemove: (md: string) => `${md}の作業日を削除`,
  workDaysTooMany: `{siteShort}の作業日は1つの{schedule}に${SCHEDULE_LIMITS.siteWorkDays}日までです`,
  /** 帯の ⋮「休日：日・土・祝」（#298。前は「休日（日・土・祝）…」） */
  offDaysItem: (offText: string) => `休日：${offText}`,
  /** 帯の ⋮「表示設定」の窓（#298、docs/102 §2.4.2：前の「表の列…」「バーの日付を常に表示」「表の幅：標準／広い／自動幅」を1つに） */
  viewItem: "表示設定",
  viewTitle: "表示設定",
  viewCols: "列",
  viewWidth: "表の幅",
  viewWidthStd: "標準",
  viewWidthWide: "広い",
  viewWidthAuto: "自動",
  viewBarDates: "バーの日付",
  viewBarDatesAlways: "常に表示",
  colStart: "開始",
  colDays: "日数",
  colState: "状況",
  colProgress: "進捗",
  colCrew: "人",
  /** 行の ⋮（列を隠したときのドラッグしない方法） */
  rowDatesItem: "開始・日数",
  rowDateItem: "開始",
  rowDatesTitle: "開始・日数",
  rowDateTitle: "開始",
  rowStateItem: "状況",
  rowProgressItem: "進捗",
  rowStateTitle: "状況",
  /** 人の列を出していないときの{task}の ⋮（#297。ドラッグしない方法・スマホも） */
  rowCrewItem: "人数",
  /** {task}の ⋮（#298：「…」を付けない。窓の題は「前の{task}を選択」のまま） */
  predsItem: "前の{task}",
  shiftItem: "後続と一括移動",
  resizerTitle: "ドラッグで表の幅（← →・ダブルクリックで自動幅）",
  rainOk: "雨天も",
  /** ⋮ の ✓ の項目（S-00009 で「雨でも作業する／雨の日は作業しない」から） */
  rainOkItem: "雨天も作業",
  /** 「雨でも」の印の説明（title） */
  rainOkOn: "雨天も作業",
  /** ⋮ の ✓ の項目（S-00009 で「休みの日も作業する／休みの日は作業しない」から） */
  offWorkItem: "休日も作業",
  /* まとめてずらす */
  shiftFromButton: "一括移動",
  shiftFromTitle: "指定日以降を一括移動",
  shiftFromDate: "この日以降",
  shiftFromDays: "移動日数（前はマイナス）",
  shiftFromDone: (n: number, days: number) => `${n}行を${days > 0 ? `${days}日後ろ` : `${-days}日前`}へ移動しました`,
  shiftFromNone: "指定日以降に始まる{task}はありません",
  /* 遅れ・出来高 */
  delayNotice: (name: string, n: number) => `${name || "（名前なし）"}が ${n}日遅れています`,
  delayMore: (n: number) => `ほか ${n}件`,
  delayKeep: "このまま",
  delayExtend: (n: number) => `${n}日延長`,
  delayBadge: (n: number) => `${n}日遅れ`,
  delayExtended: (name: string, n: number) => `「${name || "{task}"}」を${n}日延長しました`,
  planned: (n: number) => `予定 ${n}%`,
  forecast: (md: string) => `${md} の見込み`,
  earnedTitle: "出来高（今日まで）",
  earnedOf: (total: string, pct: number) => `見積 ${total} の ${pct}%`,
  amountColumn: "金額",
  amountHead: "社内用",
  /* テンプレート（#197 の続き、2026-10-17 夜 射場さん：画面の「雛形」→「テンプレート」） */
  templateSave: "テンプレート保存",
  templateSaveTitle: "テンプレート保存",
  templateName: "テンプレート名",
  templateSaved: (name: string) => `テンプレート「${name}」を保存しました`,
  templateLimit: `{schedule}のテンプレートは会社で${SCHEDULE_LIMITS.templates}までです`,
  templateNameTaken: "同じ名前のテンプレートがあります",
  templateEmpty: "{task}が無いため、テンプレートとして保存できません",
  templateRenamed: "テンプレート名を変更しました",
  templateDeleted: (name: string) => `テンプレート「${name}」を削除しました。作成済の{schedule}は変わりません`,
  /** 設定 → {schedule}のテンプレート：0件のときの文は1文だけ（docs/35 §16）。作り方は題の右の「？」（#197 の続き、10/17 夜） */
  templatesEmpty: "{schedule}のテンプレートはまだありません。",
  templatesHelp: "{schedule}の「⋮」→「テンプレート保存」で作成します。",
  /* 会社の休み */
  calendarTitle: "{schedule}の休日",
  calendarSaved: "{schedule}の休日を保存しました（作成済の{schedule}も再計算）",
  offDaysTitle: "会社の休み",
  offDayAdd: "休み追加",
  offDayName: "名前",
  offDaySpanOver: `会社の休みは1つ${SCHEDULE_LIMITS.offDaySpan}日までです`,
  offDayOrder: "「まで」は「から」より後の日にしてください",
  offDaysTooMany: `会社の休みは${SCHEDULE_LIMITS.companyOffDays}までです`,
  useCompanyOffDays: "会社の休み",
  memo: "メモ",
  assigneePick: "担当選択",
  /* ---- 3回目（#102、docs/39 §5）：全体の工程表・重なり・人数・スマホとタブレット ---- */
  overviewTitle: "{schedule}",
  running: (n: number) => `進行中 ${n}{siteShort}`,
  weekCrew: (n: number) => `今週 延べ ${n}人`,
  overlapCount: (n: number) => `重なり ${n}件`,
  bySite: "{siteShort}別",
  byVendor: "{contractor}別",
  search: "{contractor}・{siteShort}で検索",
  groupVendors: "{contractor}",
  groupUsers: "自社",
  groupText: "手入力の担当",
  groupNone: (n: number) => `担当なし ${n}{task}`,
  sites: (n: number) => `${n}{siteShort}`,
  overlapDays: (n: number) => `重なり ${n}日`,
  overlap: "重なり",
  overlapPopTitle: (days: string, n: number) => `${days} に ${n}{siteShort}`,
  openSchedule: "{schedule}表示",
  overlapNote: "{schedule}を開くと、その{task}に「重なり」の印が出ます。そこから日付を移動できます",
  crewDay: "その日の人数",
  crewDaySub: "人数を入力した{task}の合計",
  weekTotal: (n: number) => `延べ ${n}`,
  overviewTruncated: `表示できる件数を超えました。期間を短くするか、検索で絞り込んでください`,
  overviewRange: `期間は${SCHEDULE_LIMITS.overviewDays}日までです`,
  overviewOrder: "期間の終わりは始まりより後の日にしてください",
  conflictBody: (days: string) => `${days} はほかの{siteShort}にも入っています`,
  startFrom: (mdw: string) => `${mdw} から開始`,
  startedFrom: (name: string, mdw: string) => `「${name || "{task}"}」の開始を ${mdw} に変更しました`,
  alsoAt: (site: string) => `${site} にも入っています`,
  /** ⋮ の ✓ の項目（S-00009 で「人数の列を隠す」との切替から） */
  crewColumn: "人数",
  /** ⋮ の ✓ の項目（S-00009 で「金額の列を出す（社内用）／金額の列を隠す」から。右に amountHead） */
  amountColumnItem: "金額",
  crewHead: "人",
  crewLabel: "人数",
  crewPeople: (n: number) => `${n}人`,
  crewTaskOnly: "人数は{task}のみ入力できます",
  crewRange: `人数は1〜${SCHEDULE_LIMITS.crewMax}人です`,
  viewOnly: "閲覧のみ",
  edit: "編集",
  editSwitch: "編集",
  progressInput: "進捗入力",
  progressSet: (n: number) => (n >= 100 ? "進捗を済みにしました" : `進捗を${n}%にしました`),
  progressHint: "選択すると保存します。「元に戻す」で取消できます",
  progressDone: "済み",
  doneRows: (n: number) => `済み ${n}{task}`,
  /* ---- 9回目（#292、docs/102 §4）：進捗の状況（未着手／着手／完了）・実績日・設定「進捗を％で入力」 ---- */
  stateColumn: "状況",
  stateTodo: "未着手",
  stateStarted: "着手",
  stateDone: "完了",
  /** 表の「状況」のセル（押すと次の状況。title・読み上げ） */
  stateNext: (to: string) => `クリックで${to}`,
  stateSet: (name: string, to: string) => `「${name || "{task}"}」を${to}にしました`,
  /** 帯・{project}のタブ・ホーム・全体の{schedule}の進み具合（％ OFF） */
  doneOf: (n: number, m: number) => `完了 ${n}／${m}{task}`,
  /** 着手の遅れ（墨。「このまま」だけ） */
  startDelayNotice: (name: string, n: number) => `${name || "（名前なし）"}の着手が ${n}日遅れています`,
  startDelayCount: (n: number) => `着手の遅れ ${n}{task}`,
  startDelayBadge: (n: number) => `着手 ${n}日遅れ`,
  actualStart: "着手日",
  actualEnd: "完了日",
  /** 設定 → {schedule} の節「進捗」 */
  progressSection: "進捗",
  progressPctSwitch: "進捗を％で入力",
  progressPctHelp: "オフ（既定）は「未着手／着手／完了」の3つで入力し、着手日・完了日を記録します。オンにすると0〜100%で入力します（1以上にした日を着手日、100%にした日を完了日として記録）。切り替えても入力済の内容は消えません。",
  progressPctOn: "進捗を％で入力にしました",
  progressPctOff: "進捗を「未着手／着手／完了」で入力にしました",
  hintPinch: "2本指で拡大・縮小",
  hintLongPress: "バーを長押しで移動",
  phoneNote: "人数は{task}に入力した人数の合計。休日の{task}は表示しません",
  /** 人の列を出していないとき（#297）：人数の文を外す */
  phoneNoteNoCrew: "休日の{task}は表示しません",
  selfMark: "自社",
  addTask: "{task}追加",
  noWorkDay: "この日に作業する{task}はありません",
  close: "閉じる",
  /* ---- 10/1 射場さん：期間の外の工程（全体の工程表。docs/39 実装の記録「期間の外の工程」） ---- */
  showLate: "遅延{task}も表示",
  edgeAfter: (md: string) => `${md}〜`,
  edgeAfterLabel: (name: string, md: string) => `この先の{task}「${name || "（名前なし）"}」${md}〜 へ移動`,
  edgeBefore: (md: string) => `〜${md}`,
  edgeBeforeLabel: (name: string, md: string) => `前の{task}「${name || "（名前なし）"}」〜${md} へ移動`,
  lateCount: (n: number) => `遅れ ${n}件`,
  upcomingTitle: (n: number) => `この先の{task}（${n}日）`,
  overviewEmpty: (project: string) => `この期間とこの先に{schedule}のある${project}はありません`,
  /* ---- 7回目（#291、docs/102 §3）：案件なしの{schedule}・棒工程 ---- */
  create: "{schedule}作成",
  createTitle: "{schedule}作成",
  title: "題",
  titleRequired: "題を入力してください",
  titleTooLong: "題は100字までです",
  owner: "担当",
  noProject: "{project}なし",
  /** 全体の{schedule}の一番上の段 */
  standaloneSection: (n: number) => `{project}なしの{schedule} ${n}件`,
  fromEstimateNeedsProject: "{project}に紐づけると見積から作成できます",
  attach: "{project}に紐づける",
  attachTitle: "{project}に紐づける",
  attachButton: "紐づける",
  attachSearch: "{project}・{customer}で検索",
  attachEmpty: "{schedule}の無い{project}はありません",
  attachNew: "{project}作成",
  attached: (name: string) => `「${name}」に紐づけました`,
  attachedToNew: "{project}を作成し、{schedule}を紐づけました",
  partnerNotFound: "{customer}が見つかりません",
  ownerNotFound: "担当が見つかりません",
  /** 概略（見出しの行だけ・中の{task}を名前つきで並べる） */
  summary: "概略",
  summaryOn: "概略表示",
  /** 随時（{task}の ⋮ と行の印） */
  intermittent: "随時",
  intermittentOn: "随時（期間の中でときどき作業）",
  headingPlan: "計画",
  headingPlanTitle: (span: string) => `計画 ${span}`,
  /** 標準テンプレート */
  builtin: "標準",
  templateCopy: "複製",
  templateCopied: (name: string) => `「${name}」を複製しました`,
  templateHide: "非表示",
  templateShow: "表示",
  templateHidden: (name: string) => `「${name}」を非表示にしました`,
  templateShown: (name: string) => `「${name}」を表示にしました`,
  copySuffix: "（複製）",
  templateBuiltin: "標準テンプレートは変更・削除できません。「複製」で自社のテンプレートにしてから変更してください",
  /* ---- #299（docs/102 §5.10）：日のメモ ---- */
  /** 右クリックのメニュー（{task}の行の日・日付の見出し） */
  dayNoteItem: "この日のメモ",
  dayMenuOff: "休み",
  dayMenuWork: "作業",
  dayMenuSiteOff: "{siteShort}の休み",
  dayMenuSiteWork: "{siteShort}の作業日",
  dayMenuStop: "中止",
  dayMenuLabel: (mdw: string) => `${mdw}のメニュー`,
  /** 吹き出し（日付・文の欄・保存・削除） */
  dayNoteText: "メモ",
  dayNoteSave: "保存",
  dayNoteDelete: "削除",
  dayNoteSiteLabel: "{siteShort}のメモ",
  dayNoteSaved: (md: string) => `${md} のメモを保存しました`,
  dayNoteDeleted: (md: string) => `${md} のメモを削除しました`,
  dayNoteTooLong: `日のメモは${SCHEDULE_LIMITS.dayNoteText}字までです`,
  dayNotesTooMany: `日のメモは1つの{schedule}に${SCHEDULE_LIMITS.dayNotes}件までです`,
  dayNoteInvalid: "日のメモは{task}と日付の見出しのみ設定できます",
  /** 乗せたときの吹き出し（title）：「11/14（土） 資材搬入 10時」 */
  dayNoteTip: (mdw: string, text: string) => `${mdw} ${text}`,
  /** {task}の ⋮「日ごとのメモ」の窓（開始〜終わりの暦に文の欄。範囲の外のメモも残る） */
  dayNotesItem: "日ごとのメモ",
  dayNotesTitle: "日ごとのメモ",
  dayNotesOutside: "期間外",
} as const;

export const ScheduleRowKind = z.enum(["task", "heading", "memo", "milestone"]);
export type ScheduleRowKind = z.infer<typeof ScheduleRowKind>;

/* ---------------- 日付（'YYYY-MM-DD'） ---------------- */

const DAY_MS = 86_400_000;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** 1970-01-01 からの通し番号（UTC の暦で数えるので時差に左右されない） */
export function dayNo(ymd: string): number {
  const m = YMD_RE.exec(ymd);
  if (!m) throw new Error(`日付の形でない: ${ymd}`);
  return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS);
}
export function ymdOfDayNo(n: number): string {
  const d = new Date(n * DAY_MS);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
export function isValidYmd(s: string): boolean {
  if (!YMD_RE.test(s)) return false;
  return ymdOfDayNo(dayNo(s)) === s;
}
export const addDays = (ymd: string, n: number): string => ymdOfDayNo(dayNo(ymd) + n);
/** 曜日（0＝日〜6＝土） */
export const weekdayOf = (ymd: string): number => (((dayNo(ymd) + 4) % 7) + 7) % 7;
export const WEEKDAY_JA = ["日", "月", "火", "水", "木", "金", "土"] as const;
/** 「9/19」 */
export const mdOf = (ymd: string): string => `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;
/** 「9/19(土)」 */
export const mdwOf = (ymd: string): string => `${mdOf(ymd)}(${WEEKDAY_JA[weekdayOf(ymd)]})`;
/** 「9月19日(土)」 */
/** 「1/14（木）」「1/11（祝）」（#293 のトーストと吹き出し。祝日は曜日の代わりに「祝」） */
export const mdwJaOf = (ymd: string, holidays?: ReadonlySet<string>): string => `${mdOf(ymd)}（${holidays?.has(ymd) ? "祝" : WEEKDAY_JA[weekdayOf(ymd)]}）`;
export const mdwLongOf = (ymd: string): string => `${Number(ymd.slice(5, 7))}月${Number(ymd.slice(8, 10))}日(${WEEKDAY_JA[weekdayOf(ymd)]})`;
export const Ymd = z.string().refine(isValidYmd, "日付の形（YYYY-MM-DD）になっていません");

/**
 * 休みの日の決まり（工程表の off_weekdays・holiday_off と、祝日のマスタ。#101 で 会社の休み・中止の日を足した）。
 *   休みの日＝休みの曜日 ∪ 祝日（holidayOff のとき）∪ 会社の休み（工程表が使うとき）∪ 中止の日（現場全体）
 *   工程を選んだ中止（#105）はその工程の暦（calendarForRow）だけの休み
 */
export type WorkCalendar = {
  offWeekdays: readonly number[];
  holidayOff: boolean;
  holidays: ReadonlySet<string>;
  /** 会社の休み（年末年始など。工程表の useCompanyOffDays が true のときだけ入る） */
  companyOff?: ReadonlySet<string>;
  /** 中止の日（現場全体＝scope all。「休みの日も作業する」工程も止まる） */
  stops?: ReadonlySet<string>;
  /** 工程を選んだ中止（scope rows。#105）：行の id → 止めた日。その行の暦（calendarForRow）だけ休みになる */
  rowStops?: ReadonlyMap<string, ReadonlySet<string>>;
  /** {siteShort}の休みの日（#293。中止の理由 off＝stops にも入る。見出しの「休」・地色を中止と分けるのに使う） */
  siteOff?: ReadonlySet<string>;
  /** {task}の日ごとの休み・作業（#293）：行の id → 日 → 作業（true）／休み（false）。その行の暦（calendarForRow）だけに効く */
  rowDays?: ReadonlyMap<string, ReadonlyMap<string, boolean>>;
  /** その行の日ごとの指定（calendarForRow が入れる。中止・{siteShort}の休みより弱く、曜日・祝日・会社の休み・「休日も作業」より強い） */
  force?: ReadonlyMap<string, boolean>;
  /** {siteShort}の作業日（#296）：休みの日（曜日・祝日・会社の休み）でも全{task}が作業する日。{task}の日ごとの指定より弱い */
  siteWork?: ReadonlySet<string>;
};
/** workCalendar に渡す中止（日付だけなら現場全体）。reason off は{siteShort}の休み（#293） */
export type StopInput = string | { date: string; scope?: "all" | "rows"; rowIds?: readonly string[]; reason?: string };
/** workCalendar に渡す{task}の日ごとの指定（#293） */
export type RowDayInput = { rowId: string; day: string; work: boolean };
/** 期間（から〜まで、両端を含む）の日付をすべて */
export function datesBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = dayNo(from); d <= dayNo(to); d++) out.push(ymdOfDayNo(d));
  return out;
}
export function workCalendar(
  s: { offWeekdays: readonly number[]; holidayOff: boolean; useCompanyOffDays?: boolean },
  holidays: Iterable<string | { date: string }>,
  extra: { companyOffDays?: readonly { from: string; to: string }[]; stops?: Iterable<StopInput>; rowDays?: Iterable<RowDayInput>; workDays?: Iterable<string> } = {},
): WorkCalendar {
  const set = new Set<string>();
  for (const h of holidays) set.add(typeof h === "string" ? h : h.date);
  const companyOff = new Set<string>();
  if (s.useCompanyOffDays !== false) for (const o of extra.companyOffDays ?? []) for (const d of datesBetween(o.from, o.to)) companyOff.add(d);
  const stops = new Set<string>();
  const siteOff = new Set<string>();
  const rowStops = new Map<string, Set<string>>();
  for (const x of extra.stops ?? []) {
    if (typeof x === "string" || (x.scope ?? "all") === "all") {
      stops.add(typeof x === "string" ? x : x.date);
      // {siteShort}の休み（#293）は中止と同じく全{task}を止める（「休日も作業」の{task}も）。見出しの印だけ分ける
      if (typeof x !== "string" && x.reason === "off") siteOff.add(x.date);
      continue;
    }
    for (const id of x.rowIds ?? []) {
      const ds = rowStops.get(id) ?? new Set<string>();
      ds.add(x.date);
      rowStops.set(id, ds);
    }
  }
  const rowDays = new Map<string, Map<string, boolean>>();
  for (const x of extra.rowDays ?? []) {
    const m = rowDays.get(x.rowId) ?? new Map<string, boolean>();
    m.set(x.day, x.work);
    rowDays.set(x.rowId, m);
  }
  const siteWork = new Set<string>(extra.workDays ?? []);
  return { offWeekdays: s.offWeekdays, holidayOff: s.holidayOff, holidays: set, companyOff, stops, rowStops, siteOff, rowDays, siteWork };
}
/**
 * 休みの日か（#293 で強さの順に。#296 で{siteShort}の作業日を足した）：中止・{siteShort}の休み（stops）＞ その行の日ごとの指定（force。calendarForRow が入れる）＞
 * {siteShort}の作業日（siteWork）＞ 休みの曜日・祝日（holidayOff のとき）・会社の休み。「休日も作業」の{task}は calendarForRow が曜日・祝日・会社の休みを外した暦にする
 */
export function isOffDay(ymd: string, cal: WorkCalendar): boolean {
  if (cal.stops?.has(ymd)) return true;
  const f = cal.force?.get(ymd);
  if (f !== undefined) return !f;
  if (cal.siteWork?.has(ymd)) return false;
  return isBaseOffDay(ymd, cal);
}
/** 暦の休みの日か（休みの曜日・祝日（holidayOff のとき）・会社の休み。中止・指定・作業日は見ない） */
export const isBaseOffDay = (ymd: string, cal: Pick<WorkCalendar, "offWeekdays" | "holidayOff" | "holidays" | "companyOff">): boolean =>
  cal.offWeekdays.includes(weekdayOf(ymd)) || (cal.holidayOff && cal.holidays.has(ymd)) || !!cal.companyOff?.has(ymd);
/** {siteShort}の作業日か（#296。見出しの地色を外し曜日を墨に。暦の休みの日でなければ false＝効いていない） */
export const isSiteWorkDay = (ymd: string, cal: WorkCalendar): boolean => !!cal.siteWork?.has(ymd) && !cal.stops?.has(ymd) && isBaseOffDay(ymd, cal);
/** 現場全体の中止の日か（見出しの ☂・「止」に使う。{siteShort}の休みは入れない＝isSiteOffDay） */
export const isStopDay = (ymd: string, cal: WorkCalendar): boolean => !!cal.stops?.has(ymd) && !cal.siteOff?.has(ymd);
/** {siteShort}の休みの日か（#293。見出しの「休」・休みの日と同じ地色） */
export const isSiteOffDay = (ymd: string, cal: WorkCalendar): boolean => !!cal.siteOff?.has(ymd);
/** その行が止まっている日（現場全体の中止と、その行を選んだ中止）か */
export const isRowStopDay = (ymd: string, rowId: string, cal: WorkCalendar): boolean => !!cal.stops?.has(ymd) || !!cal.rowStops?.get(rowId)?.has(ymd);
/** 全部の曜日が休みなら稼働日が無い（DB も API も止める）。念のため 3660日（約10年）で打ち切る */
const MAX_SCAN = 3660;
/** その日が休みなら次の稼働日（dir=-1 なら前の稼働日）へ */
export function snapToWorkDay(ymd: string, cal: WorkCalendar, dir: 1 | -1 = 1): string {
  let d = ymd;
  for (let i = 0; i < MAX_SCAN && isOffDay(d, cal); i++) d = addDays(d, dir);
  return d;
}
/** 次の稼働日（その日より後） */
export const nextWorkDay = (ymd: string, cal: WorkCalendar): string => snapToWorkDay(addDays(ymd, 1), cal);
/** 稼働日で n日動かす（マイナスは前へ）。休みの日から動かすときも、数えるのは稼働日だけ */
export function addWorkDays(ymd: string, n: number, cal: WorkCalendar): string {
  if (n === 0) return ymd;
  const dir = n > 0 ? 1 : -1;
  let d = ymd;
  let left = Math.abs(n);
  for (let i = 0; i < MAX_SCAN * 3 && left > 0; i++) {
    d = addDays(d, dir);
    if (!isOffDay(d, cal)) left--;
  }
  return d;
}
/** 工程の終わり＝開始（休みなら次の稼働日）から日数ぶん稼働日を数えた日 */
export function endOfTask(start: string, days: number, cal: WorkCalendar): string {
  const s = snapToWorkDay(start, cal);
  return days <= 1 ? s : addWorkDays(s, days - 1, cal);
}
/** a〜b（両端を含む）の稼働日の数。b が a より前なら 0 */
export function workDaysBetween(a: string, b: string, cal: WorkCalendar): number {
  const s = dayNo(a);
  const e = dayNo(b);
  let n = 0;
  for (let d = s; d <= e; d++) if (!isOffDay(ymdOfDayNo(d), cal)) n++;
  return n;
}

/* ---------------- 行・つなぎ・工程表 ---------------- */

/** 担当：業者（partners）・自社の人（users）・そのまま書いた文字。担当なしは null。name は API が付ける表示用 */
export const ScheduleAssignee = z.discriminatedUnion("type", [
  z.object({ type: z.literal("partner"), id: z.string().uuid(), name: z.string().optional() }),
  z.object({ type: z.literal("user"), id: z.string().uuid(), name: z.string().optional() }),
  z.object({ type: z.literal("text"), text: z.string().trim().min(1).max(SCHEDULE_LIMITS.assigneeText, SCHEDULE_MSG.assigneeTooLong) }),
]);
export type ScheduleAssignee = z.infer<typeof ScheduleAssignee>;

export const ScheduleRow = z
  .object({
    /** 画面が作った uuid（工程表の中で動かしても変わらない） */
    id: z.string().uuid(),
    kind: ScheduleRowKind,
    /** 工程名・見出し・節目の名前（200字）／メモの文（500字） */
    name: z.string().default(""),
    /** 見出しの説明（200字。見出しだけ） */
    note: z.string().max(SCHEDULE_LIMITS.note, SCHEDULE_MSG.noteTooLong).nullable().default(null),
    /** 開始（節目はその日）。空なら日付未定 */
    start: Ymd.nullable().default(null),
    /** 日数＝稼働日の数（工程だけ） */
    days: z.number().int().min(1).max(SCHEDULE_LIMITS.workDays).nullable().default(null),
    /** 終わり。API が計算する（送った値は使わない） */
    end: Ymd.nullable().default(null),
    /**
     * 進み（工程 0〜100、節目 0＝まだ／100＝済み）。#292：％ OFF の会社（既定）は 状況（taskStateOf）で読む＝100 は完了・1〜99 は着手。
     * 状況の切り替え（setTaskState）は 未着手＝0・着手＝そのまま（着手日を入れる）・完了＝100
     */
    progress: z.number().int().min(0).max(100).nullable().default(null),
    /**
     * 実績の着手日・完了日（{task}だけ。#292）。完了日があれば着手日もある（着手日 ≦ 完了日）。完了でなくなれば完了日は消す（tidyRow）。
     * PUT で省けば今のまま（API が保存してある値を入れる＝前の画面から保存しても消えない）。答えにはいつも入る
     */
    actualStart: Ymd.nullable().optional(),
    actualEnd: Ymd.nullable().optional(),
    assignee: ScheduleAssignee.nullable().default(null),
    /** 見出しをたたんでいる（全員共通） */
    collapsed: z.boolean().default(false),
    /** 休みの日も作業する（工程だけ。true なら工程表の休みの曜日・祝日も日数に数える。9/30 射場さん） */
    workOffDays: z.boolean().default(false),
    /** 雨でも作業する（工程だけ。#105）。中止のダイアログ（工程を選ぶ）で最初からチェックを外す。日数の数え方は変えない */
    rainOk: z.boolean().default(false),
    /** 随時（工程だけ。#291）。バーは点線。遅れ・予定の進み・出来高の予定から外す（完了だけ見る）。人数は数える */
    intermittent: z.boolean().default(false),
    /**
     * 見積から写した金額（税抜・円。工程だけ。#101）。API が DB（fn_schedule_estimate_items）から入れて返す＝表示用。
     * PUT で送っても使わない（同じ id の行は DB の値のまま、新しい id の行は空）
     */
    amount: z.number().int().nullable().default(null),
    /** 金額の出どころの見積（表示用。PUT で送っても使わない） */
    sourceDocumentId: z.string().uuid().nullable().default(null),
    /** 見積のどの大項目か（sheet:<系譜>／line:<系譜>。表示用） */
    sourceRef: z.string().max(80).nullable().default(null),
    /** 「このまま」で覚えた遅れの日数（工程だけ。遅れがこれより増えるまで知らせない。全員共通） */
    delayAckDays: z.number().int().min(0).max(SCHEDULE_LIMITS.workDays).default(0),
    /** 「着手の遅れ」の「このまま」で覚えた日数（{task}だけ。#292。％ OFF の会社だけ使う） */
    startAckDays: z.number().int().min(0).max(SCHEDULE_LIMITS.workDays).default(0),
    /** 人数（工程だけ・1〜99・空でよい。#102）。「その日の人数」「今週 延べ n人」と2行目の「・n人」 */
    crew: z.number().int().min(1, SCHEDULE_MSG.crewRange).max(SCHEDULE_LIMITS.crewMax, SCHEDULE_MSG.crewRange).nullable().default(null),
    /** 反映した写真の数（#103。API が付ける表示用。PUT で送っても使わない） */
    photoCount: z.number().int().min(0).optional(),
  })
  .superRefine((r, ctx) => {
    const max = r.kind === "memo" ? SCHEDULE_LIMITS.memo : SCHEDULE_LIMITS.name;
    if (r.name.length > max) ctx.addIssue({ code: "custom", path: ["name"], message: r.kind === "memo" ? SCHEDULE_MSG.memoTooLong : SCHEDULE_MSG.nameTooLong });
    // 人数は工程だけ（API は 400。DB の CHECK も同じ）
    if (r.crew != null && r.kind !== "task") ctx.addIssue({ code: "custom", path: ["crew"], message: SCHEDULE_MSG.crewTaskOnly });
  });
export type ScheduleRow = z.infer<typeof ScheduleRow>;

/** つなぎ：from の工程が終わったら to の工程が始まる */
export const ScheduleLink = z.object({ from: z.string().uuid(), to: z.string().uuid() });
export type ScheduleLink = z.infer<typeof ScheduleLink>;

export const OffWeekdays = z
  .array(z.number().int().min(0).max(6))
  .max(6, SCHEDULE_MSG.offWeekdaysAll)
  .refine((xs) => new Set(xs).size === xs.length, "同じ曜日が2つあります");

/** 中止の日（#101。1日1つ）。reason：rain＝雨（☂）／other＝ほか（「止」・メモ）／off＝{siteShort}の休み（「休」。#293。{siteShort}全体だけ） */
export const StopReason = z.enum(["rain", "other", "off"]);
export type StopReason = z.infer<typeof StopReason>;
/** 中止の対象（#105）：all＝現場全体（台風など。#101 からの中止）／rows＝選んだ工程だけ */
export const StopScope = z.enum(["all", "rows"]);
export type StopScope = z.infer<typeof StopScope>;
export const ScheduleStop = z
  .object({
    date: Ymd,
    reason: StopReason.default("rain"),
    note: z.string().trim().max(SCHEDULE_LIMITS.stopNote, SCHEDULE_MSG.stopNoteTooLong).nullable().default(null),
    scope: StopScope.default("all"),
    /** 止めた工程（scope rows のとき1つ以上。この工程表の工程だけ＝API が見る。all のときは空） */
    rowIds: z.array(z.string().uuid()).max(SCHEDULE_LIMITS.rows).default([]),
  })
  .superRefine((x, ctx) => {
    if (x.scope === "rows" && !x.rowIds.length) ctx.addIssue({ code: "custom", path: ["rowIds"], message: SCHEDULE_MSG.stopPickRows });
    if (x.scope === "all" && x.rowIds.length) ctx.addIssue({ code: "custom", path: ["rowIds"], message: SCHEDULE_MSG.stopRowInvalid });
    if (x.reason === "off" && x.scope !== "all") ctx.addIssue({ code: "custom", path: ["scope"], message: SCHEDULE_MSG.stopOffRows });
    if (new Set(x.rowIds).size !== x.rowIds.length) ctx.addIssue({ code: "custom", path: ["rowIds"], message: SCHEDULE_MSG.rowDuplicate });
  });
export type ScheduleStop = z.infer<typeof ScheduleStop>;

/**
 * {task}の日ごとの休み・作業（#293、docs/102 §5）。work false＝その日はこの{task}だけ休み／true＝休みの日でもこの{task}は作業。
 * 中止・{siteShort}の休みのほうが強い（isWorkDay）。範囲の外の指定は消さず無視する。テンプレート・複写・複製には写さない
 */
export const ScheduleRowDay = z.object({ rowId: z.string().uuid(), day: Ymd, work: z.boolean() });
export type ScheduleRowDay = z.infer<typeof ScheduleRowDay>;

/**
 * 日のメモ（#299、docs/102 §5.10）。rowId あり＝その{task}のその日のメモ／null＝日付の見出しのメモ（{siteShort}のメモ）。
 * メモは日付に付く＝{task}を動かしても動かない・範囲の外でも消さない（日ごとの休み・作業と違う）。テンプレート・複写・複製には写さない
 */
export const ScheduleDayNote = z.object({
  rowId: z.string().uuid().nullable(),
  day: Ymd,
  text: z.string().trim().min(1).max(SCHEDULE_LIMITS.dayNoteText, SCHEDULE_MSG.dayNoteTooLong),
});
export type ScheduleDayNote = z.infer<typeof ScheduleDayNote>;

/** 会社の休み（年末年始・お盆などの期間。#101）。31日まで */
export const CompanyOffDay = z
  .object({ from: Ymd, to: Ymd, name: z.string().trim().max(SCHEDULE_LIMITS.offDayName).default("") })
  .superRefine((o, ctx) => {
    if (o.from > o.to) ctx.addIssue({ code: "custom", path: ["to"], message: SCHEDULE_MSG.offDayOrder });
    else if (dayNo(o.to) - dayNo(o.from) + 1 > SCHEDULE_LIMITS.offDaySpan) ctx.addIssue({ code: "custom", path: ["to"], message: SCHEDULE_MSG.offDaySpanOver });
  });
export type CompanyOffDay = z.infer<typeof CompanyOffDay>;

export const Schedule = z.object({
  id: z.string().uuid(),
  /** 案件（#291 から NULL＝案件なしの{schedule}。あとから1回だけ紐づけられる） */
  groupId: z.string().uuid().nullable(),
  /** 題（案件なしの{schedule}。紐づけたあとも残るが、画面は案件名を出す。#291） */
  title: z.string().default(""),
  /** {customer}（案件なしの{schedule}。#291）。name は略称・honorific は敬称（宛名） */
  partner: z.object({ id: z.string().uuid(), name: z.string(), fullName: z.string(), honorific: z.string().default("") }).nullable().default(null),
  /** 担当（案件なしの{schedule}。#291） */
  owner: z.object({ id: z.string().uuid(), name: z.string() }).nullable().default(null),
  /** 保存のたびに＋1。PUT の baseVersion と違えば 409 SCHEDULE_CONFLICT */
  version: z.number().int(),
  offWeekdays: z.array(z.number().int()),
  holidayOff: z.boolean(),
  /** 会社の休み（ScheduleView.companyOffDays）をこの工程表の休みにするか（#101） */
  useCompanyOffDays: z.boolean().default(true),
  /** 中止の日（#101。日付の順） */
  stops: z.array(ScheduleStop).default([]),
  /** {task}の日ごとの休み・作業（#293。工程表の上から・日付の順） */
  rowDays: z.array(ScheduleRowDay).default([]),
  /** {siteShort}の作業日（#296。日付の順。休みの日でも全{task}が作業する日） */
  workDays: z.array(Ymd).default([]),
  /** 日のメモ（#299。日付の見出しのメモが先・{task}は上から・日付の順） */
  dayNotes: z.array(ScheduleDayNote).default([]),
  rows: z.array(ScheduleRow),
  links: z.array(ScheduleLink),
  updatedAt: z.string(),
  /** 最後に直した人の名前（空なら出さない） */
  updatedBy: z.string(),
});
export type Schedule = z.infer<typeof Schedule>;

export const Holiday = z.object({ date: z.string(), name: z.string() });
export type Holiday = z.infer<typeof Holiday>;

/**
 * 重なり（#102、docs/39 §5.2 の4・8）：この工程表の工程の担当（業者か自社の人）が、同じ日にほかの現場の工程にも入っている。
 *   days＝重なった日（この工程の稼働日のうち、相手の稼働日でもある日）。others＝相手（現場・工程・期間・人数）
 */
export const ScheduleConflictOther = z.object({
  /** 相手の{schedule}（#291。案件なしの{schedule}は groupId が null） */
  scheduleId: z.string().uuid(),
  groupId: z.string().uuid().nullable(),
  groupName: z.string(),
  rowId: z.string().uuid(),
  rowName: z.string(),
  start: z.string(),
  end: z.string(),
  crew: z.number().int().nullable(),
});
export type ScheduleConflictOther = z.infer<typeof ScheduleConflictOther>;
export const ScheduleConflict = z.object({ rowId: z.string().uuid(), days: z.array(z.string()), others: z.array(ScheduleConflictOther) });
export type ScheduleConflict = z.infer<typeof ScheduleConflict>;

/* ---- 協力会社からの報告（#103、docs/39 §6。計算と画面の文は scheduleReport.ts） ---- */
export const ScheduleReportKind = z.enum(["done", "stop", "delay", "photo"]);
export type ScheduleReportKind = z.infer<typeof ScheduleReportKind>;
export const ScheduleReportReason = z.enum(["material", "people", "weather", "before", "other"]);
export type ScheduleReportReason = z.infer<typeof ScheduleReportReason>;
export const ScheduleReportStatus = z.enum(["pending", "applied", "dismissed", "withdrawn"]);
export type ScheduleReportStatus = z.infer<typeof ScheduleReportStatus>;
/** 会社の画面の報告（R1・案件の工程表の知らせの行） */
export const ScheduleReport = z.object({
  id: z.string().uuid(),
  kind: ScheduleReportKind,
  status: ScheduleReportStatus,
  partner: z.object({ id: z.string().uuid(), name: z.string() }),
  /** {project}（案件なしの{schedule}は id が null・name は題。#291） */
  group: z.object({ id: z.string().uuid().nullable(), name: z.string() }),
  /** {schedule}の id（#291） */
  scheduleId: z.string().uuid(),
  /** 行（消えていれば null） */
  row: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  workDate: z.string(),
  delayDays: z.number().int().nullable(),
  reason: ScheduleReportReason.nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
  decidedBy: z.string(),
  decidedNote: z.string().nullable(),
  photos: z.array(z.object({ id: z.string().uuid() })),
  /** こうなります（確かめ待ちだけ。反映すると工程表がどうなるか） */
  effect: z.string().nullable(),
  /** 反映できない（工程が消えた・担当が変わった・もう済み） */
  stale: z.boolean(),
});
export type ScheduleReport = z.infer<typeof ScheduleReport>;
/** 反映した写真（案件の工程表の「写真 n」。だれがいつ送ったか） */
export const ScheduleRowPhoto = z.object({
  id: z.string().uuid(),
  rowId: z.string().uuid(),
  reportId: z.string().uuid(),
  partnerName: z.string(),
  sentAt: z.string(),
  note: z.string().nullable(),
});
export type ScheduleRowPhoto = z.infer<typeof ScheduleRowPhoto>;

/**
 * GET・POST・PUT /groups/:id/schedule の答え。祝日は 工程表の最初の日の前の年〜最後の日の次の年（無ければ今年と来年）。
 * companyOffDays＝会社の休み（#101。工程表の useCompanyOffDays が true のとき休みに数える）。
 * conflicts＝重なり（#102。比べる期間はこの工程表の工期。API が conflictsFor で数える）
 */
export const ScheduleView = z.object({
  schedule: Schedule.nullable(),
  holidays: z.array(Holiday),
  companyOffDays: z.array(CompanyOffDay).default([]),
  conflicts: z.array(ScheduleConflict).default([]),
  /** 協力会社からの報告の確かめ待ち（#103。この工程表の分） */
  reports: z.array(ScheduleReport).default([]),
  /** 反映した写真（#103。工程の「写真 n」） */
  photos: z.array(ScheduleRowPhoto).default([]),
  /** 会社の設定「進捗を％で入力」（#292。false＝未着手／着手／完了の3つ。#292 より前の答えには無い＝％） */
  progressPct: z.boolean().default(true),
});
export type ScheduleView = z.infer<typeof ScheduleView>;

/** PUT /groups/:id/schedule（工程表まるごと） */
export const ScheduleSave = z.object({
  baseVersion: z.number().int(),
  offWeekdays: OffWeekdays,
  holidayOff: z.boolean(),
  rows: z.array(ScheduleRow).max(SCHEDULE_LIMITS.rows, SCHEDULE_MSG.tooManyRows),
  links: z.array(ScheduleLink).max(SCHEDULE_LIMITS.links, SCHEDULE_MSG.tooManyLinks),
  /** 会社の休みを使うか（#101。省けば今のまま） */
  useCompanyOffDays: z.boolean().optional(),
  /** 中止の日（#101。工程表まるごと。省けば今のまま＝1回目の画面から送られても消えない） */
  stops: z
    .array(ScheduleStop)
    .max(SCHEDULE_LIMITS.stops, SCHEDULE_MSG.stopTooMany)
    .refine((xs) => new Set(xs.map((x) => x.date)).size === xs.length, "同じ日の中止が2つあります")
    .optional(),
  /** {task}の日ごとの休み・作業（#293。工程表まるごと。省けば今のまま） */
  rowDays: z
    .array(ScheduleRowDay)
    .max(SCHEDULE_LIMITS.rowDays, SCHEDULE_MSG.rowDaysTooMany)
    .refine((xs) => new Set(xs.map((x) => `${x.rowId}|${x.day}`)).size === xs.length, "同じ日の指定が2つあります")
    .optional(),
  /** {siteShort}の作業日（#296。工程表まるごと。省けば今のまま） */
  workDays: z
    .array(Ymd)
    .max(SCHEDULE_LIMITS.siteWorkDays, SCHEDULE_MSG.workDaysTooMany)
    .refine((xs) => new Set(xs).size === xs.length, "同じ日の作業日が2つあります")
    .optional(),
  /** 日のメモ（#299。工程表まるごと。省けば今のまま） */
  dayNotes: z
    .array(ScheduleDayNote)
    .max(SCHEDULE_LIMITS.dayNotes, SCHEDULE_MSG.dayNotesTooMany)
    .refine((xs) => new Set(xs.map((x) => `${x.rowId ?? ""}|${x.day}`)).size === xs.length, "同じ日のメモが2つあります")
    .optional(),
});
export type ScheduleSave = z.input<typeof ScheduleSave>;

/** POST /schedules（案件なしの{schedule}。#291）。題は必須（★2）・{customer}は任意・担当は省けば作成者 */
export const ScheduleCreate = z.object({
  title: z.string().trim().min(1, SCHEDULE_MSG.titleRequired).max(100, SCHEDULE_MSG.titleTooLong),
  partnerId: z.string().uuid().nullable().default(null),
  ownerUserId: z.string().uuid().nullable().optional(),
});
export type ScheduleCreate = z.input<typeof ScheduleCreate>;
/** POST /schedules/:id/attach（案件なしの{schedule}を{schedule}の無い{project}に紐づける。#291） */
export const ScheduleAttach = z.object({ groupId: z.string().uuid() });
export type ScheduleAttach = z.infer<typeof ScheduleAttach>;
/** 紐づける先の候補（GET /schedule/attach-candidates?q=）＝{schedule}の無い{project} */
export const ScheduleAttachCandidate = z.object({ id: z.string().uuid(), name: z.string(), clientName: z.string(), updatedAt: z.string() });
export type ScheduleAttachCandidate = z.infer<typeof ScheduleAttachCandidate>;

/** {schedule}の画面の道（#291）：案件があれば /groups/:id/schedule、案件なしは /schedules/:id */
export function scheduleHref(x: { groupId: string | null; scheduleId?: string | null; id?: string }, rowId?: string | null): string {
  const base = x.groupId ? `/groups/${x.groupId}/schedule` : `/schedules/${x.scheduleId ?? x.id ?? ""}`;
  return rowId ? `${base}?row=${rowId}` : base;
}
/** {schedule}の API の道（#291）：案件があれば /groups/:id/schedule、案件なしは /schedules/:id（どちらも同じ処理） */
export function scheduleApiBase(x: { groupId: string | null; scheduleId?: string | null; id?: string }): string {
  return x.groupId ? `/groups/${x.groupId}/schedule` : `/schedules/${x.scheduleId ?? x.id ?? ""}`;
}

/** 案件の画面のタブ「工程」の要約（GroupDetail.schedule） */
export const ScheduleBrief = z.object({
  start: z.string().nullable(),
  end: z.string().nullable(),
  /** 進み具合（%）。工程が無ければ null */
  progress: z.number().int().nullable(),
  taskCount: z.number().int(),
  /** 日付のある節目（日付の順） */
  milestones: z.array(z.object({ name: z.string(), date: z.string(), done: z.boolean() })),
  /** 出来高（#101。金額のある工程が無ければ null）：earned＝Σ（金額 × 進み）、total＝Σ金額（税抜・円） */
  earned: z.object({ earned: z.number().int(), total: z.number().int() }).nullable().default(null),
  /** いちばん大きい遅れ（日。「このまま」で覚えた分までは数えない）。遅れが無ければ 0 */
  maxDelay: z.number().int().default(0),
  /** 完了した{task}の数（#292。「完了 n／m{task}」の n。m は taskCount） */
  doneCount: z.number().int().default(0),
  /** 会社の設定「進捗を％で入力」（#292。false なら画面は「完了 n／m{task}」、progress は完了した{task}の日数の割合） */
  pct: z.boolean().default(true),
});
export type ScheduleBrief = z.infer<typeof ScheduleBrief>;

/**
 * 担当の候補の業者・{顧客}（#102 §5.1 の1）：name＝略称（空なら「株式会社」などを外した名前）、fullName＝正式名、
 * kana＝フリガナ（画面の中で探すため。無ければ空）
 */
export const ScheduleAssigneePartner = z.object({ id: z.string().uuid(), name: z.string(), fullName: z.string(), kana: z.string().default("") });
export type ScheduleAssigneePartner = z.infer<typeof ScheduleAssigneePartner>;
/** 担当の候補（GET /schedule/assignees） */
export const ScheduleAssignees = z.object({
  vendors: z.array(ScheduleAssigneePartner),
  users: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /** この案件の{顧客} */
  clients: z.array(ScheduleAssigneePartner),
});
export type ScheduleAssignees = z.infer<typeof ScheduleAssignees>;

/* ---------------- 計算（画面と API が同じものを使う） ---------------- */

export type ScheduleDoc = { rows: ScheduleRow[]; links: ScheduleLink[] };
const LINKABLE: readonly ScheduleRowKind[] = ["task", "milestone"];
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

const NO_DAYS: ReadonlySet<string> = new Set<string>();
/** 行ごとの暦の控え（同じ暦・同じ行からは同じものを返す） */
const rowCalOf = new WeakMap<WorkCalendar, Map<string, WorkCalendar>>();
/**
 * その行が日数を数える暦：「休みの日も作業する」工程は 中止の日だけが休みの暦（#101：現場が止まるので止まる）、
 * ほかは工程表の暦（9/30 射場さん「基本土日祝は休みですが工程によっては休みでも工程できる仕組みがほしい」）。
 * 中止は 現場全体の中止と、その工程を選んだ中止（#105）だけが休み。画面のバー・ドラッグ・キーも同じものを使う
 */
export function calendarForRow(r: Pick<ScheduleRow, "kind"> & { id?: string | undefined; workOffDays?: boolean | undefined }, cal: WorkCalendar): WorkCalendar {
  const every = r.kind === "task" && !!r.workOffDays;
  const own = r.kind === "task" && r.id ? cal.rowStops?.get(r.id) : undefined;
  // {task}の日ごとの指定（#293）：その行の暦だけに効く（中止・{siteShort}の休みより弱い＝isOffDay）
  const force = r.kind === "task" && r.id ? cal.rowDays?.get(r.id) : undefined;
  if (!every && !own?.size && !force?.size) return cal;
  const key = `${every ? 1 : 0}:${own?.size || force?.size ? r.id : ""}`;
  let m = rowCalOf.get(cal);
  if (!m) {
    m = new Map();
    rowCalOf.set(cal, m);
  }
  const had = m.get(key);
  if (had) return had;
  const stops = own?.size ? new Set([...(cal.stops ?? NO_DAYS), ...own]) : (cal.stops ?? NO_DAYS);
  const f = { ...(force?.size ? { force } : {}), ...(cal.siteOff ? { siteOff: cal.siteOff } : {}) };
  const c: WorkCalendar = every
    ? { offWeekdays: [], holidayOff: false, holidays: NO_DAYS, companyOff: NO_DAYS, stops, ...f }
    : { offWeekdays: cal.offWeekdays, holidayOff: cal.holidayOff, holidays: cal.holidays, companyOff: cal.companyOff ?? NO_DAYS, stops, ...f, ...(cal.siteWork?.size ? { siteWork: cal.siteWork } : {}) };
  m.set(key, c);
  return c;
}

/**
 * その{task}がその日に作業するか（#293、docs/102 §5.3）。強さ：①中止（{siteShort}全体・その{task}を選んだもの）と{siteShort}の休み → 作業しない
 * ②日ごとの指定（schedule_row_days）があればそのとおり ③{siteShort}の作業日（schedule_work_days。#296）なら作業
 * ④無ければ今までどおり（曜日・祝日・会社の休み。「休日も作業」ならそれらも作業）。
 * 終わりの計算（addWorkDays・normalizeSchedule）・遅れ・人数・見出しの期間・協力会社の今日の予定は、同じ calendarForRow の暦を通るのでこれと同じ答え
 */
export function isWorkDay(r: Pick<ScheduleRow, "kind"> & { id?: string | undefined; workOffDays?: boolean | undefined }, ymd: string, cal: WorkCalendar): boolean {
  return !isOffDay(ymd, calendarForRow(r, cal));
}

/** バーの日の状態（#293。画面のクリック・⋮ の暦）：why＝stop（中止）／siteOff（{siteShort}の休み）／set（日ごとの指定）／base（既定の決まり） */
export type RowDayWhy = "stop" | "siteOff" | "set" | "base";
export function rowDayState(
  r: Pick<ScheduleRow, "kind"> & { id: string; workOffDays?: boolean | undefined },
  ymd: string,
  cal: WorkCalendar,
): { work: boolean; why: RowDayWhy; base: boolean; baseWhy: "holiday" | "company" | "weekday" | null } {
  const every = r.kind === "task" && !!r.workOffDays;
  const hol = cal.holidayOff && cal.holidays.has(ymd);
  const company = !!cal.companyOff?.has(ymd);
  const weekday = cal.offWeekdays.includes(weekdayOf(ymd));
  // {siteShort}の作業日（#296）は既定が作業（理由は出さない）
  const baseWhy = every || cal.siteWork?.has(ymd) ? null : hol ? "holiday" : company ? "company" : weekday ? "weekday" : null;
  const base = baseWhy == null;
  if (cal.siteOff?.has(ymd)) return { work: false, why: "siteOff", base, baseWhy };
  if (cal.stops?.has(ymd) || cal.rowStops?.get(r.id)?.has(ymd)) return { work: false, why: "stop", base, baseWhy };
  const f = cal.rowDays?.get(r.id)?.get(ymd);
  if (f !== undefined) return { work: f, why: "set", base, baseWhy };
  return { work: base, why: "base", base, baseWhy };
}
/**
 * バーの日をクリックしたとき（#293、docs/102 §5.4）：働く日 → 休み、休みの日 → 作業。既定と同じになる指定は消す（指定は既定と違う日だけ持つ）。
 * 中止・{siteShort}の休みの日は変えない（null）
 */
export function toggleRowDay(
  rowDays: readonly ScheduleRowDay[],
  r: Pick<ScheduleRow, "kind"> & { id: string; workOffDays?: boolean | undefined },
  ymd: string,
  cal: WorkCalendar,
): { rowDays: ScheduleRowDay[]; work: boolean } | null {
  if (r.kind !== "task") return null;
  const st = rowDayState(r, ymd, cal);
  if (st.why === "stop" || st.why === "siteOff") return null;
  return { rowDays: setRowDay(rowDays, r.id, ymd, !st.work, st.base), work: !st.work };
}
/** その日の指定を work にする（既定 base と同じなら指定を消す）。並びは 日付の順 */
export function setRowDay(rowDays: readonly ScheduleRowDay[], rowId: string, ymd: string, work: boolean, base: boolean): ScheduleRowDay[] {
  const rest = rowDays.filter((x) => !(x.rowId === rowId && x.day === ymd));
  return work === base ? rest : [...rest, { rowId, day: ymd, work }].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
}
/**
 * 範囲の外の指定を消す（#296、docs/102 §5.9。§5.1 の4 を改めた）：その{task}の開始〜終わりの外にある指定・日付の無い{task}の指定を外す。
 * rows はそろえたあと（normalizeSchedule の答え）。範囲の外の指定は終わりの計算に効かないので、消しても行の日付は変わらない
 */
export function pruneRowDays(rowDays: readonly ScheduleRowDay[], rows: readonly Pick<ScheduleRow, "id" | "kind" | "start" | "end">[]): ScheduleRowDay[] {
  const span = new Map(rows.filter((r) => r.kind === "task" && r.start).map((r) => [r.id, [r.start!, r.end ?? r.start!] as const]));
  return rowDays.filter((x) => {
    const sp = span.get(x.rowId);
    return !!sp && x.day >= sp[0] && x.day <= sp[1];
  });
}
/**
 * 開始を休みの指定のある日に置いたとき（#296、docs/102 §5.9 の2）：その日の休みの指定を外す（開始が後ろへ逃げない）。
 * 開始が前と変わった{task}だけを見る（バーの最初の日を押して休みにしたときは開始が変わらない＝今までどおり次の稼働日へ）
 */
export function releaseStartRowDays(
  before: readonly Pick<ScheduleRow, "id" | "start">[],
  after: readonly Pick<ScheduleRow, "id" | "kind" | "start">[],
  rowDays: readonly ScheduleRowDay[],
): ScheduleRowDay[] {
  const prev = new Map(before.map((r) => [r.id, r.start]));
  const moved = new Map(after.filter((r) => r.kind === "task" && r.start && prev.has(r.id) && prev.get(r.id) !== r.start).map((r) => [r.id, r.start!]));
  if (!moved.size) return [...rowDays];
  return rowDays.filter((x) => !(x.work === false && moved.get(x.rowId) === x.day));
}
/**
 * 開始をその日に置くとき（ドラッグ・左端。#296）：その{task}の休みの指定がある日（中止・{siteShort}の休みでない）ならその日のまま
 * （保存のときに releaseStartRowDays が指定を外す）。ほかは今までどおり稼働日へ
 */
export function snapStartOf(r: Pick<ScheduleRow, "kind"> & { id?: string | undefined; workOffDays?: boolean | undefined }, ymd: string, cal: WorkCalendar): string {
  const rc = calendarForRow(r, cal);
  if (r.kind === "task" && rc.force?.get(ymd) === false && !rc.stops?.has(ymd)) return ymd;
  return snapToWorkDay(ymd, rc);
}
/**
 * ドラッグの途中の見本の暦（#296）：開始を動かした先がその{task}の休みの指定のある日なら、その指定を外した暦（離したあとの保存と同じ形に見せる）
 */
export function calendarReleasing(cal: WorkCalendar, r: Pick<ScheduleRow, "id" | "kind" | "start">, start: string | null | undefined): WorkCalendar {
  if (r.kind !== "task" || !start || start === r.start || cal.rowDays?.get(r.id)?.get(start) !== false) return cal;
  const rowDays = new Map(cal.rowDays);
  const own = new Map(rowDays.get(r.id));
  own.delete(start);
  rowDays.set(r.id, own);
  return { ...cal, rowDays };
}
/**
 * 日付の見出しをクリックしたとき（#296、docs/102 §5.8 の3）：中止 → "stop"（画面は中止のポップ）／{siteShort}の休み → 解除／
 * {siteShort}の作業日 → 解除（休みの日に戻る）／暦の休みの日（曜日・祝日・会社の休み）→ {siteShort}の作業日／平日 → {siteShort}の休み
 */
export function toggleSiteDay(
  stops: readonly ScheduleStop[],
  workDays: readonly string[],
  date: string,
  cal: Pick<WorkCalendar, "offWeekdays" | "holidayOff" | "holidays" | "companyOff">,
):
  | { action: "stop" }
  | { action: "offAdded" | "offRemoved"; stops: ScheduleStop[]; workDays: string[] }
  | { action: "workAdded" | "workRemoved"; stops: ScheduleStop[]; workDays: string[] } {
  const have = stops.find((x) => x.date === date);
  if (have && have.reason !== "off") return { action: "stop" };
  if (have) return { action: "offRemoved", stops: stops.filter((x) => x.date !== date), workDays: [...workDays] };
  if (workDays.includes(date)) return { action: "workRemoved", stops: [...stops], workDays: workDays.filter((d) => d !== date) };
  if (isBaseOffDay(date, cal)) return { action: "workAdded", stops: [...stops], workDays: [...workDays, date].sort() };
  const add: ScheduleStop = { date, reason: "off", note: null, scope: "all", rowIds: [] };
  return { action: "offAdded", stops: [...stops, add].sort(byDate), workDays: [...workDays] };
}
/* ---------------- 日のメモ（#299、docs/102 §5.10） ---------------- */
/** その日のメモ（rowId null＝日付の見出しのメモ）。無ければ null */
export function dayNoteOf(notes: readonly ScheduleDayNote[], rowId: string | null, day: string): ScheduleDayNote | null {
  return notes.find((x) => x.rowId === rowId && x.day === day) ?? null;
}
/**
 * その日のメモを text にする（前後の空白を除く。空なら消す）。並びは 日付の見出しのメモが先・同じ行の中は日付の順（行の順はそろえるときに）。
 * 同じ日のメモは1つ（上書き）
 */
export function setDayNote(notes: readonly ScheduleDayNote[], rowId: string | null, day: string, text: string): ScheduleDayNote[] {
  const rest = notes.filter((x) => !(x.rowId === rowId && x.day === day));
  const t = text.trim().slice(0, SCHEDULE_LIMITS.dayNoteText);
  if (!t) return rest;
  return [...rest, { rowId, day, text: t }];
}
/**
 * 日のメモをそろえる（画面は変えるたび・API は受け取ったメモに）：無い行・{task}でない行のメモを外す（日付の見出しのメモは残す）・同じ日は後ろの1つ。
 * 並びは 日付の見出しのメモ（日付の順）→ 行の上から（日付の順）。範囲の外のメモは消さない（メモは日付に付く）
 */
export function tidyDayNotes(notes: readonly ScheduleDayNote[], rows: readonly Pick<ScheduleRow, "id" | "kind">[]): ScheduleDayNote[] {
  const pos = new Map(rows.filter((r) => r.kind === "task").map((r, i) => [r.id, i]));
  const out = new Map<string, ScheduleDayNote>();
  for (const x of notes) {
    if (x.rowId !== null && !pos.has(x.rowId)) continue;
    const t = x.text.trim().slice(0, SCHEDULE_LIMITS.dayNoteText);
    if (!t) continue;
    out.set(`${x.rowId ?? ""}|${x.day}`, { rowId: x.rowId, day: x.day, text: t });
  }
  const rank = (x: ScheduleDayNote) => (x.rowId === null ? -1 : pos.get(x.rowId)!);
  return [...out.values()].sort((a, b) => rank(a) - rank(b) || (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
}
/** 行ごとのメモ（行の id → 日付 → 文。日付の見出しのメモは キー ""） */
export function dayNotesByRow(notes: readonly ScheduleDayNote[]): Map<string, Map<string, string>> {
  const m = new Map<string, Map<string, string>>();
  for (const x of notes) {
    const k = x.rowId ?? "";
    const own = m.get(k) ?? new Map<string, string>();
    own.set(x.day, x.text);
    m.set(k, own);
  }
  return m;
}

/** 日ごとの指定をそろえる（画面は変えるたび・API は受け取った指定に）：無い行・{task}でない行の指定を外す。同じ行の同じ日は後ろの1つ */
export function tidyRowDays(rowDays: readonly ScheduleRowDay[], rows: readonly Pick<ScheduleRow, "id" | "kind">[]): ScheduleRowDay[] {
  const tasks = new Set(rows.filter((r) => r.kind === "task").map((r) => r.id));
  const out = new Map<string, ScheduleRowDay>();
  for (const x of rowDays) if (tasks.has(x.rowId)) out.set(`${x.rowId}|${x.day}`, { rowId: x.rowId, day: x.day, work: x.work });
  return [...out.values()];
}
/**
 * 日付の見出しをクリックしたとき（#293）：{siteShort}の休みでなければ{siteShort}の休みにし、{siteShort}の休みなら解除する。
 * その日が中止（雨・ほか）なら変えない（"stop"＝画面は中止のポップを開く＝黙って上書きしない）
 */
export function toggleSiteOff(stops: readonly ScheduleStop[], date: string): { stops: ScheduleStop[]; action: "added" | "removed" } | { action: "stop" } {
  const have = stops.find((x) => x.date === date);
  if (have && have.reason !== "off") return { action: "stop" };
  if (have) return { stops: stops.filter((x) => x.date !== date), action: "removed" };
  const add: ScheduleStop = { date, reason: "off", note: null, scope: "all", rowIds: [] };
  return { stops: [...stops, add].sort(byDate), action: "added" };
}

/** 種類ごとに持てるものだけにそろえる（DB の CHECK と同じ形）。工程の開始は稼働日へ、終わりを計算する */
function tidyRow(r0: ScheduleRow, cal: WorkCalendar): ScheduleRow {
  // 1回目の画面・雛形から来た行には 2回目の列が無いことがある
  const r: ScheduleRow = {
    ...r0,
    amount: r0.amount ?? null,
    sourceDocumentId: r0.sourceDocumentId ?? null,
    sourceRef: r0.sourceRef ?? null,
    delayAckDays: r0.delayAckDays ?? 0,
    startAckDays: r0.startAckDays ?? 0,
    rainOk: !!r0.rainOk,
    intermittent: !!r0.intermittent,
    crew: r0.crew == null ? null : clamp(Math.round(r0.crew), 1, SCHEDULE_LIMITS.crewMax),
    actualStart: r0.actualStart ?? null,
    actualEnd: r0.actualEnd ?? null,
  };
  const notTask = { amount: null, sourceDocumentId: null, sourceRef: null, delayAckDays: 0, startAckDays: 0, rainOk: false, intermittent: false, crew: null, actualStart: null, actualEnd: null };
  const base = { ...r, note: null, collapsed: false, workOffDays: false, ...(r.kind === "task" ? {} : notTask) };
  switch (r.kind) {
    case "heading": {
      // 見出しは手で入れた期間と担当を持てる（#291 ★3）。日数は稼働日（工程表の暦）。進みは持たない（中の工程で数える）
      const h = { ...r, ...notTask, progress: null, workOffDays: false };
      if (!r.start || r.days == null) return { ...h, start: null, days: null, end: null };
      const start = snapToWorkDay(r.start, cal);
      const days = clamp(Math.round(r.days), 1, SCHEDULE_LIMITS.workDays);
      return { ...h, start, days, end: endOfTask(start, days, cal) };
    }
    case "memo":
      return { ...base, start: null, days: null, end: null, progress: null, assignee: null };
    case "milestone":
      return { ...base, days: null, end: r.start, progress: (r.progress ?? 0) >= 100 ? 100 : 0 };
    case "task": {
      const progress = clamp(Math.round(r.progress ?? 0), 0, 100);
      const workOffDays = !!r.workOffDays;
      const actual = tidyActuals(progress, r.actualStart ?? null, r.actualEnd ?? null);
      if (!r.start || r.days == null) return { ...base, ...actual, start: null, days: null, end: null, progress, workOffDays };
      const rc = calendarForRow(r, cal);
      const start = snapToWorkDay(r.start, rc);
      const days = clamp(Math.round(r.days), 1, SCHEDULE_LIMITS.workDays);
      return { ...base, ...actual, start, days, end: endOfTask(start, days, rc), progress, workOffDays };
    }
  }
}

/** 使えるつなぎだけ（両端が 工程か節目・自分自身でない・同じ組は1本） */
function tidyLinks(rows: ScheduleRow[], links: ScheduleLink[]): ScheduleLink[] {
  const kind = new Map(rows.map((r) => [r.id, r.kind]));
  const seen = new Set<string>();
  return links.filter((l) => {
    const k = `${l.from}>${l.to}`;
    if (l.from === l.to || seen.has(k)) return false;
    const a = kind.get(l.from);
    const b = kind.get(l.to);
    if (!a || !b || !LINKABLE.includes(a) || !LINKABLE.includes(b)) return false;
    seen.add(k);
    return true;
  });
}

/** つなぎの並び（前 → 後ろ）。輪があれば輪の中の行は入らない */
function topoOrder(ids: string[], links: ScheduleLink[]): string[] {
  const indeg = new Map(ids.map((id) => [id, 0]));
  const out = new Map<string, string[]>();
  for (const l of links) {
    if (!indeg.has(l.from) || !indeg.has(l.to)) continue;
    indeg.set(l.to, indeg.get(l.to)! + 1);
    out.set(l.from, [...(out.get(l.from) ?? []), l.to]);
  }
  const q = ids.filter((id) => indeg.get(id) === 0);
  const order: string[] = [];
  while (q.length) {
    const id = q.shift()!;
    order.push(id);
    for (const t of out.get(id) ?? []) {
      indeg.set(t, indeg.get(t)! - 1);
      if (indeg.get(t) === 0) q.push(t);
    }
  }
  return order;
}

/** 輪があるか */
export function hasCycle(links: ScheduleLink[]): boolean {
  const ids = [...new Set(links.flatMap((l) => [l.from, l.to]))];
  return topoOrder(ids, links).length < ids.length;
}
/** from → to をつなぐと輪になるか（自分自身も） */
export function wouldCreateCycle(links: ScheduleLink[], from: string, to: string): boolean {
  if (from === to) return true;
  const next = new Map<string, string[]>();
  for (const l of links) next.set(l.from, [...(next.get(l.from) ?? []), l.to]);
  const seen = new Set<string>([to]);
  const q = [to];
  while (q.length) {
    const id = q.shift()!;
    if (id === from) return true;
    for (const t of next.get(id) ?? []) {
      if (seen.has(t)) continue;
      seen.add(t);
      q.push(t);
    }
  }
  return false;
}

/** 前の工程が終わったあと、この行が始められる最初の日（前が節目なら同じ日から）。cal はこの行（後ろ）の暦 */
function earliestAfter(pred: ScheduleRow, cal: WorkCalendar): string | null {
  if (!pred.start) return null;
  if (pred.kind === "milestone") return pred.start;
  return pred.end ? nextWorkDay(pred.end, cal) : null;
}

/**
 * 工程表をそろえる（docs/39 §3.4）：種類ごとの形・工程の開始を稼働日へ・終わりの計算・つなぎの押し出し。
 *   前の工程とぶつかる後ろの工程だけを押し出す（日数は変えない。つながった先へ次々）。前へ動かしても後ろは引き寄せない
 */
export function normalizeSchedule(doc: ScheduleDoc, cal: WorkCalendar): ScheduleDoc {
  const rows = doc.rows.map((r) => tidyRow(r, cal));
  const links = tidyLinks(rows, doc.links);
  const byId = new Map(rows.map((r) => [r.id, r]));
  const preds = new Map<string, string[]>();
  for (const l of links) preds.set(l.to, [...(preds.get(l.to) ?? []), l.from]);
  for (const id of topoOrder(rows.map((r) => r.id), links)) {
    const r = byId.get(id)!;
    if (!r.start || !preds.has(id)) continue;
    const rc = calendarForRow(r, cal);
    let earliest: string | null = null;
    for (const p of preds.get(id)!) {
      const e = earliestAfter(byId.get(p)!, rc);
      if (e && (!earliest || e > earliest)) earliest = e;
    }
    if (!earliest || r.start >= earliest) continue;
    const moved: ScheduleRow =
      r.kind === "task" && r.days != null
        ? { ...r, start: snapToWorkDay(earliest, rc), end: endOfTask(earliest, r.days, rc) }
        : { ...r, start: earliest, end: earliest };
    byId.set(id, moved);
  }
  return { rows: rows.map((r) => byId.get(r.id)!), links };
}

/** その行と、つながった後ろの行すべての id */
export function successorsOf(links: ScheduleLink[], id: string): Set<string> {
  const out = new Set<string>([id]);
  const q = [id];
  while (q.length) {
    const x = q.shift()!;
    for (const l of links) {
      if (l.from !== x || out.has(l.to)) continue;
      out.add(l.to);
      q.push(l.to);
    }
  }
  return out;
}

/** 「後ろとまとめてずらす n日」：その行と、つながった後ろ全部を稼働日で n日（マイナス可）動かしてそろえる */
export function shiftWithSuccessors(doc: ScheduleDoc, id: string, n: number, cal: WorkCalendar): ScheduleDoc {
  const ids = successorsOf(doc.links, id);
  const rows = doc.rows.map((r) => {
    if (!ids.has(r.id) || !r.start || (r.kind !== "task" && r.kind !== "milestone")) return r;
    const rc = calendarForRow(r, cal);
    return { ...r, start: addWorkDays(snapToWorkDay(r.start, rc, n < 0 ? -1 : 1), n, rc) };
  });
  return normalizeSchedule({ rows, links: doc.links }, cal);
}

/* ---------------- 見出しと進み ---------------- */

/** 行の塊：見出しなら 見出し〜次の見出しの手前、ほかは その行だけ。[start, end) */
export function blockOf(rows: readonly ScheduleRow[], i: number): [number, number] {
  if (rows[i]?.kind !== "heading") return [i, i + 1];
  let j = i + 1;
  while (j < rows.length && rows[j]!.kind !== "heading") j++;
  return [i, j];
}
/** その行が入っている見出しの位置（見出しより前の行・見出しそのものは -1） */
export function headingIndexOf(rows: readonly ScheduleRow[], i: number): number {
  if (rows[i]?.kind === "heading") return -1;
  for (let k = i - 1; k >= 0; k--) if (rows[k]!.kind === "heading") return k;
  return -1;
}
/** たたんだ見出しの中の行を除いた行（表示する行） */
export function visibleRows(rows: readonly ScheduleRow[]): ScheduleRow[] {
  const out: ScheduleRow[] = [];
  let hidden = false;
  for (const r of rows) {
    if (r.kind === "heading") {
      hidden = r.collapsed;
      out.push(r);
    } else if (!hidden) out.push(r);
  }
  return out;
}

/**
 * 進み具合＝Σ（日数 × 進み）÷ Σ日数。工程だけ（節目・メモ・日付未定は入れない）。工程が無ければ null。
 * #292：pct が false（％ OFF の会社）なら 完了した{task}の日数 ÷ 全日数（着手・保存してある 1〜99％ は 0 と数える）
 */
export function progressOf(rows: readonly ScheduleRow[], pct = true): number | null {
  let w = 0;
  let s = 0;
  for (const r of rows) {
    if (r.kind !== "task" || r.days == null) continue;
    w += r.days;
    s += r.days * pctOf(r, pct);
  }
  return w ? Math.round(s / w) : null;
}
export const scheduleProgress = (rows: readonly ScheduleRow[], pct = true): number | null => progressOf(rows, pct);
/** 計算に使う進み（％ OFF は 完了＝100・ほか 0） */
const pctOf = (r: Pick<ScheduleRow, "progress"> & { actualStart?: string | null | undefined }, pct: boolean): number =>
  pct ? Math.max(0, Math.min(100, r.progress ?? 0)) : taskStateOf(r) === "done" ? 100 : 0;
/** 完了した{task}の数と{task}の数（#292。帯の「完了 n／m{task}」） */
export function doneCountOf(rows: readonly ScheduleRow[]): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const r of rows) {
    if (r.kind !== "task") continue;
    total++;
    if (taskStateOf(r) === "done") done++;
  }
  return { done, total };
}

/** 期間＝中の工程・節目の最初の開始〜最後の終わり */
function spanOf(rows: readonly ScheduleRow[], kinds: readonly ScheduleRowKind[]): { start: string | null; end: string | null } {
  let start: string | null = null;
  let end: string | null = null;
  for (const r of rows) {
    if (!kinds.includes(r.kind) || !r.start) continue;
    const e = r.end ?? r.start;
    if (!start || r.start < start) start = r.start;
    if (!end || e > end) end = e;
  }
  return { start, end };
}

/**
 * 見出しのまとめ：期間は 中に日付のある工程があればその範囲（inner）、無ければ手で入れた期間（#291 ★3）。進み（日数の重み）は中の工程だけ。
 * plan＝手で入れた期間（無ければ null）。中に工程が入ったあとも残る（画面・紙は薄い枠で描き、計画と実際の差が見える）
 */
export function headingSummary(
  rows: readonly ScheduleRow[],
  i: number,
  pct = true,
): { start: string | null; end: string | null; inner: boolean; plan: { start: string; end: string } | null; progress: number | null; count: number } {
  const [a, b] = blockOf(rows, i);
  const inner = rows.slice(a + 1, b);
  const span = spanOf(inner, ["task"]);
  const h = rows[i];
  const plan = h?.kind === "heading" && h.start && h.end ? { start: h.start, end: h.end } : null;
  return {
    start: span.start ?? plan?.start ?? null,
    end: span.end ?? plan?.end ?? null,
    inner: !!span.start,
    plan,
    progress: progressOf(inner, pct),
    count: inner.length,
  };
}

/** 案件の画面のタブの要約。today と暦があれば遅れも数える（#101）。pct＝会社の設定「進捗を％で入力」（#292。省けば％） */
export function scheduleBriefOf(rows: readonly ScheduleRow[], at?: { today: string; cal: WorkCalendar }, pct = true): ScheduleBrief {
  const ms = rows
    .filter((r): r is ScheduleRow & { start: string } => r.kind === "milestone" && !!r.start)
    .map((r) => ({ name: r.name, date: r.start, done: (r.progress ?? 0) >= 100 }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const maxDelay = at ? rows.reduce((m, r) => Math.max(m, shownDelayOf(r, at.today, at.cal, pct)), 0) : 0;
  const dc = doneCountOf(rows);
  return {
    // 工期：工程・節目と、手で期間を入れた見出し（#291）
    ...spanOf(rows, ["task", "milestone", "heading"]),
    progress: progressOf(rows, pct),
    taskCount: dc.total,
    doneCount: dc.done,
    pct,
    milestones: ms,
    earned: earnedOf(rows, pct),
    maxDelay,
  };
}
/** 次の節目（今日から先で、まだ済んでいない最初のもの） */
export function nextMilestoneOf(brief: Pick<ScheduleBrief, "milestones">, today: string): { name: string; date: string; daysLeft: number } | null {
  const m = brief.milestones.find((x) => !x.done && x.date >= today);
  return m ? { name: m.name, date: m.date, daysLeft: dayNo(m.date) - dayNo(today) } : null;
}
/** 最後の節目（例「引き渡し」）。無ければ null（画面は「終わり」で最後の工程の終わり） */
export function lastMilestoneOf(brief: Pick<ScheduleBrief, "milestones">): { name: string; date: string } | null {
  const m = brief.milestones[brief.milestones.length - 1];
  return m ? { name: m.name, date: m.date } : null;
}

/* ---------------- 行の操作（並べ替え・消す・足す） ---------------- */

/** 行（見出しなら中の行ごと）を gap（元の並びでの差し込み位置 0〜長さ）へ動かす */
export function moveBlockTo<T extends ScheduleRow>(rows: readonly T[], i: number, gap: number): T[] {
  const [a, b] = blockOf(rows, i);
  if (gap >= a && gap <= b) return [...rows];
  const block = rows.slice(a, b);
  const rest = [...rows.slice(0, a), ...rows.slice(b)];
  const at = gap > b ? gap - (b - a) : gap;
  return [...rest.slice(0, at), ...block, ...rest.slice(at)];
}

/**
 * 「上へ」「下へ」（Alt＋↑↓）。見出しは中の行ごと、前（後ろ）の塊を1つ越える。ほかの行は1つずつ（見出しをまたぐと その見出しの中へ・外へ）。
 * 動かせなければ同じ並びを返す
 */
export function moveRow<T extends ScheduleRow>(rows: readonly T[], i: number, dir: -1 | 1): T[] {
  const r = rows[i];
  if (!r) return [...rows];
  if (r.kind !== "heading") {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return [...rows];
    const out = [...rows];
    out[i] = rows[j]!;
    out[j] = r;
    return out;
  }
  const [a, b] = blockOf(rows, i);
  if (dir < 0) {
    if (a === 0) return [...rows];
    const h = headingIndexOf(rows, a - 1);
    const to = rows[a - 1]!.kind === "heading" ? a - 1 : h >= 0 ? h : a - 1;
    return moveBlockTo(rows, a, to);
  }
  if (b >= rows.length) return [...rows];
  const [, nb] = blockOf(rows, b);
  return moveBlockTo(rows, a, nb);
}

/** 消す：その行（mode='block' なら見出しの中の行も）と、消した行につながるつなぎ */
export function removeRows(doc: ScheduleDoc, i: number, mode: "row" | "block" = "row"): ScheduleDoc {
  const [a, b] = mode === "block" ? blockOf(doc.rows, i) : [i, i + 1];
  const gone = new Set(doc.rows.slice(a, b).map((r) => r.id));
  return { rows: doc.rows.filter((r) => !gone.has(r.id)), links: doc.links.filter((l) => !gone.has(l.from) && !gone.has(l.to)) };
}

/** 新しい工程・節目の既定の日：すぐ上の工程の終わりの次の稼働日（無ければ今日） */
export function defaultStartAt(rows: readonly ScheduleRow[], at: number, today: string, cal: WorkCalendar): string {
  for (let k = at - 1; k >= 0; k--) {
    const r = rows[k]!;
    if (r.kind === "task" && r.end) return nextWorkDay(r.end, cal);
    if (r.kind === "milestone" && r.start) return snapToWorkDay(r.start, cal);
  }
  return snapToWorkDay(today, cal);
}

/** 新しい行（id は呼ぶ側が作る） */
export function newRow(kind: ScheduleRowKind, id: string, start: string | null): ScheduleRow {
  const x = { amount: null, sourceDocumentId: null, sourceRef: null, delayAckDays: 0, startAckDays: 0, collapsed: false, workOffDays: false, rainOk: false, intermittent: false, assignee: null, crew: null, actualStart: null, actualEnd: null };
  switch (kind) {
    case "task":
      return { ...x, id, kind, name: "", note: null, start, days: start ? 1 : null, end: null, progress: 0 };
    case "milestone":
      return { ...x, id, kind, name: "", note: null, start, days: null, end: start, progress: 0 };
    case "heading":
      return { ...x, id, kind, name: "", note: "", start: null, days: null, end: null, progress: null };
    case "memo":
      return { ...x, id, kind, name: "", note: null, start: null, days: null, end: null, progress: null };
  }
}

/** 複製：新しい id。金額・見積のつながり・覚えた遅れは写さない（出来高が二重に数えられないように。#101）。実績日も写さない（#292） */
export function copyRow(r: ScheduleRow, id: string): ScheduleRow {
  return { ...r, id, collapsed: false, amount: null, sourceDocumentId: null, sourceRef: null, delayAckDays: 0, startAckDays: 0, actualStart: null, actualEnd: null };
}

/* ============================================================================
 * 2回目（#101、docs/39 §4）：遅れ・予定の進み・見込み・出来高・雛形・見積から作る・中止・まとめてずらす
 * ========================================================================== */

/**
 * 遅れ（日。docs/39 §4.1 の9）：始まっている工程で、昨日までに終わっているはずの日数 − 進み×日数 を切り捨てて 1日以上。
 * 今日の分はまだ遅れにしない（当日の朝から「遅れ」と出さない）。進み 100%・日付未定・工程でない行は 0。
 * #292：pct が false（％ OFF の会社。既定）なら endDelayOf＝終わりの日を過ぎて完了していない{task}だけ
 */
export function delayOf(r: ScheduleRow, today: string, cal: WorkCalendar, pct = true): number {
  if (!pct) return endDelayOf(r, today, cal);
  // 随時の工程は遅れに出さない（#291。完了だけ見る）
  if (r.kind !== "task" || r.intermittent || !r.start || r.days == null) return 0;
  const p = r.progress ?? 0;
  if (p >= 100) return 0;
  const yesterday = addDays(today, -1);
  if (yesterday < r.start) return 0;
  const until = r.end && r.end < yesterday ? r.end : yesterday;
  const expected = Math.min(r.days, workDaysBetween(r.start, until, calendarForRow(r, cal)));
  // 整数で数える（進み×日数 ÷ 100 の小数で揺れないように）
  const d = Math.floor((expected * 100 - p * r.days) / 100);
  return d >= 1 ? d : 0;
}
/**
 * 終わりの遅れ（#292、docs/102 §4。％ OFF）：終わりの日を過ぎて完了していない{task}。n＝終わりの次の日〜今日の稼働日の数（今日が休みでも 1 日以上）。
 * 随時・日付未定・完了は 0。「n日延長」で日数を n 増やすと終わりが今日になり 0 に戻る
 */
export function endDelayOf(r: ScheduleRow, today: string, cal: WorkCalendar): number {
  if (r.kind !== "task" || r.intermittent || !r.start || !r.end || r.days == null) return 0;
  if (taskStateOf(r) === "done" || today <= r.end) return 0;
  return Math.max(1, workDaysBetween(addDays(r.end, 1), today, calendarForRow(r, cal)));
}
/**
 * 着手の遅れ（#292、docs/102 §4。％ OFF だけ）：開始日を過ぎて未着手の{task}。n＝開始日〜昨日の稼働日の数（今日の分はまだ遅れにしない）。
 * 終わりの日も過ぎていれば終わりの遅れ（endDelayOf）のほうを知らせる＝こちらは 0。随時・日付未定・着手・完了は 0
 */
export function startDelayOf(r: ScheduleRow, today: string, cal: WorkCalendar): number {
  if (r.kind !== "task" || r.intermittent || !r.start || r.days == null) return 0;
  if (taskStateOf(r) !== "todo" || today <= r.start || endDelayOf(r, today, cal) > 0) return 0;
  return workDaysBetween(r.start, addDays(today, -1), calendarForRow(r, cal));
}
/**
 * 知らせる遅れ：覚えた日数（delayAckDays）を超えた分。「このまま」はいまの遅れを覚える、「n日延ばす」は延ばした日数を覚える。
 * 覚えた分までは 0（増えるまで出さない）。増えたら増えた分だけ知らせる（延ばした翌日にまた全部を延ばさないように）
 */
export function shownDelayOf(r: ScheduleRow, today: string, cal: WorkCalendar, pct = true): number {
  const d = delayOf(r, today, cal, pct);
  return Math.max(0, d - (r.delayAckDays ?? 0));
}
/** 知らせる着手の遅れ（#292。％ OFF だけ・覚えた日数 startAckDays を超えた分） */
export function shownStartDelayOf(r: ScheduleRow, today: string, cal: WorkCalendar, pct = false): number {
  if (pct) return 0;
  return Math.max(0, startDelayOf(r, today, cal) - (r.startAckDays ?? 0));
}
/** 「このまま」：いまの遅れを覚える */
export function keepDelay(r: ScheduleRow, today: string, cal: WorkCalendar, pct = true): ScheduleRow {
  return { ...r, delayAckDays: Math.min(SCHEDULE_LIMITS.workDays, delayOf(r, today, cal, pct)) };
}
/** 着手の遅れの「このまま」（#292）：いまの着手の遅れを覚える */
export function keepStartDelay(r: ScheduleRow, today: string, cal: WorkCalendar): ScheduleRow {
  return { ...r, startAckDays: Math.min(SCHEDULE_LIMITS.workDays, startDelayOf(r, today, cal)) };
}
/** 「n日延ばす」：知らせている遅れの日数だけ日数を増やし、その分を覚える（後ろは normalizeSchedule が押し出す） */
export function extendByDelay(r: ScheduleRow, today: string, cal: WorkCalendar, pct = true): ScheduleRow {
  const n = shownDelayOf(r, today, cal, pct);
  if (!n || r.days == null) return r;
  return { ...r, days: Math.min(SCHEDULE_LIMITS.workDays, r.days + n), delayAckDays: Math.min(SCHEDULE_LIMITS.workDays, (r.delayAckDays ?? 0) + n) };
}
/** 遅れが無くなった工程は覚えた日数を 0 に戻す（次に遅れたときにまた知らせる）。着手の遅れも同じ（#292） */
export function settleDelayAck<T extends ScheduleRow>(rows: readonly T[], today: string, cal: WorkCalendar, pct = true): T[] {
  return rows.map((r) => {
    if (r.kind !== "task") return r;
    const end = (r.delayAckDays ?? 0) > 0 && delayOf(r, today, cal, pct) === 0;
    const start = (r.startAckDays ?? 0) > 0 && (pct || startDelayOf(r, today, cal) === 0);
    return end || start ? { ...r, ...(end ? { delayAckDays: 0 } : {}), ...(start ? { startAckDays: 0 } : {}) } : r;
  });
}
/** 遅れている工程（知らせるものだけ。遅れの大きい順・同じなら上の行から） */
export function delayedRows(rows: readonly ScheduleRow[], today: string, cal: WorkCalendar, pct = true): { row: ScheduleRow; delay: number }[] {
  return rows
    .map((row, i) => ({ row, i, delay: shownDelayOf(row, today, cal, pct) }))
    .filter((x) => x.delay > 0)
    .sort((a, b) => b.delay - a.delay || a.i - b.i)
    .map(({ row, delay }) => ({ row, delay }));
}
/** 着手が遅れている{task}（#292。％ OFF だけ・知らせるものだけ。遅れの大きい順・同じなら上の行から） */
export function startDelayedRows(rows: readonly ScheduleRow[], today: string, cal: WorkCalendar, pct = false): { row: ScheduleRow; delay: number }[] {
  if (pct) return [];
  return rows
    .map((row, i) => ({ row, i, delay: shownStartDelayOf(row, today, cal) }))
    .filter((x) => x.delay > 0)
    .sort((a, b) => b.delay - a.delay || a.i - b.i)
    .map(({ row, delay }) => ({ row, delay }));
}
/** 予定の進み（%）：昨日までに終わっているはずの日数で数えた全体の進み（日数の重み）。工程が無ければ null */
export function plannedProgressOf(rows: readonly ScheduleRow[], today: string, cal: WorkCalendar): number | null {
  const yesterday = addDays(today, -1);
  let w = 0;
  let s = 0;
  for (const r of rows) {
    // 随時の工程は予定の進みに数えない（#291）
    if (r.kind !== "task" || r.intermittent || !r.start || r.days == null) continue;
    w += r.days;
    if (yesterday < r.start) continue;
    const until = r.end && r.end < yesterday ? r.end : yesterday;
    s += Math.min(r.days, workDaysBetween(r.start, until, calendarForRow(r, cal)));
  }
  return w ? Math.round((s * 100) / w) : null;
}
/**
 * 見込み：遅れている工程（知らせる遅れ）をその日数だけ延ばしてそろえた形（docs/39 §4.1 の11）。
 * つながった後ろだけ押される＝最後の節目の見込みの日は、遅れた工程からつないだ先だけ動く
 */
export function projectOf(doc: ScheduleDoc, today: string, cal: WorkCalendar, pct = true): ScheduleDoc {
  const rows = doc.rows.map((r) => {
    const d = shownDelayOf(r, today, cal, pct);
    return d > 0 && r.days != null ? { ...r, days: Math.min(SCHEDULE_LIMITS.workDays, r.days + d) } : r;
  });
  return normalizeSchedule({ rows, links: doc.links }, cal);
}
/**
 * 出来高（docs/39 §4.1 の11・決めごと7＝表示だけ）：Σ（金額 × 進み ÷ 100）と Σ金額。1円未満は切り捨て。金額のある工程が無ければ null。
 * #292：pct が false（％ OFF）なら 完了した{task}の金額の合計
 */
export function earnedOf(rows: readonly ScheduleRow[], pct = true): { earned: number; total: number } | null {
  let total = 0;
  let e100 = 0;
  let n = 0;
  for (const r of rows) {
    if (r.kind !== "task" || r.amount == null) continue;
    n++;
    total += r.amount;
    e100 += r.amount * pctOf(r, pct);
  }
  return n ? { earned: Math.floor(e100 / 100), total } : null;
}
/** 見出しの中の金額の合計（金額の列。中に金額が無ければ null） */
export function headingAmountOf(rows: readonly ScheduleRow[], i: number): number | null {
  const [a, b] = blockOf(rows, i);
  let n = 0;
  let sum = 0;
  for (const r of rows.slice(a + 1, b)) {
    if (r.kind !== "task" || r.amount == null) continue;
    n++;
    sum += r.amount;
  }
  return n ? sum : null;
}

/* ============================================================================
 * 9回目（#292、docs/102 §4）：進捗の状況（未着手／着手／完了）・実績日（着手日・完了日）・設定「進捗を％で入力」
 *   progress の列は残す。％ OFF の会社（既定）は 状況で読み、保存してある％は 1〜99＝着手・100＝完了 として読む（データは変えない）
 * ========================================================================== */

export const TaskState = z.enum(["todo", "started", "done"]);
export type TaskState = z.infer<typeof TaskState>;
/** 状況の言葉（表の「状況」の列・スマホのボタン・紙・CSV） */
export const TASK_STATE_LABEL: Record<TaskState, string> = { todo: "未着手", started: "着手", done: "完了" };
/** {task}の状況：完了＝進み 100／着手＝着手日がある・保存してある進みが 1〜99／未着手＝そのほか */
export function taskStateOf(r: { progress: number | null; actualStart?: string | null | undefined }): TaskState {
  const p = r.progress ?? 0;
  if (p >= 100) return "done";
  if (r.actualStart || p > 0) return "started";
  return "todo";
}
/** 表の「状況」のセルを押したとき：未着手 → 着手 → 完了 → 未着手 */
export function nextTaskState(s: TaskState): TaskState {
  return s === "todo" ? "started" : s === "started" ? "done" : "todo";
}
/**
 * 状況を変えた行（％ OFF の会社。表のセル・スマホのボタン・協力会社の報告の反映）：
 *   未着手＝進み 0・着手日と完了日を消す／着手＝着手日（無ければ today）・完了日を消す・進み 100 なら 0（それ以外の進みはそのまま）／
 *   完了＝進み 100・完了日（もう完了なら今の完了日、無ければ today）・着手日（無ければ完了日）
 */
export function setTaskState<T extends Pick<ScheduleRow, "progress"> & { actualStart?: string | null | undefined; actualEnd?: string | null | undefined }>(r: T, to: TaskState, today: string): T {
  const cur = taskStateOf(r);
  if (to === "todo") return { ...r, progress: 0, actualStart: null, actualEnd: null };
  if (to === "started") return { ...r, progress: (r.progress ?? 0) >= 100 ? 0 : (r.progress ?? 0), actualStart: r.actualStart ?? today, actualEnd: null };
  const end = cur === "done" && r.actualEnd ? r.actualEnd : today;
  const start = r.actualStart && r.actualStart <= end ? r.actualStart : end;
  return { ...r, progress: 100, actualStart: start, actualEnd: end };
}
/**
 * ％で入力した行（％ ON の会社）：最初に 1 以上にした日を着手日、100 にした日を完了日に入れる。0 に戻せば両方を消す・100 未満に戻せば完了日を消す
 */
export function setProgressPct<T extends Pick<ScheduleRow, "progress"> & { actualStart?: string | null | undefined; actualEnd?: string | null | undefined }>(r: T, p0: number, today: string): T {
  const p = Math.max(0, Math.min(100, Math.round(p0)));
  if (p <= 0) return { ...r, progress: 0, actualStart: null, actualEnd: null };
  const wasDone = (r.progress ?? 0) >= 100;
  const end = p >= 100 ? (wasDone && r.actualEnd ? r.actualEnd : today) : null;
  const start0 = r.actualStart ?? today;
  return { ...r, progress: p, actualStart: end && start0 > end ? end : start0, actualEnd: end };
}
/** 状況の字の完了の印（表・紙。#292） */
export const STATE_DONE_MARK = "✓";
/** 状況の字（紙・全体の{schedule}の行・読み上げ）：未着手＝空・着手＝「着手」・完了＝✓（#292） */
export function stateMarkOf(r: { progress: number | null; actualStart?: string | null | undefined }): string {
  const st = taskStateOf(r);
  return st === "done" ? STATE_DONE_MARK : st === "started" ? TASK_STATE_LABEL.started : "";
}
/** バーの済んだ所（％）。％ ON は進みのまま、％ OFF は 完了＝100・着手＝50・未着手＝0（#292、紙と画面のバー） */
export function barDonePct(r: { progress: number | null; actualStart?: string | null | undefined }, pct: boolean): number {
  if (pct) return Math.max(0, Math.min(100, r.progress ?? 0));
  const st = taskStateOf(r);
  return st === "done" ? 100 : st === "started" ? 50 : 0;
}
/** 見出しの状況（％ OFF。#292）：中の{task}の「完了 n／m」を短く「n／m」。中に{task}が無ければ空 */
export function headingDoneText(rows: readonly ScheduleRow[], i: number): string {
  const [a, b] = blockOf(rows, i);
  const dc = doneCountOf(rows.slice(a + 1, b));
  return dc.total ? `${dc.done}／${dc.total}` : "";
}
/** 実績日の形をそろえる（tidyRow）：完了でなければ完了日なし・完了日があれば着手日も（無ければ完了日）・着手日 ≦ 完了日 */
function tidyActuals(progress: number, a0: string | null, b0: string | null): { actualStart: string | null; actualEnd: string | null } {
  const b = progress >= 100 ? b0 : null;
  const a = b && (!a0 || a0 > b) ? b : a0;
  return { actualStart: a, actualEnd: b };
}
/**
 * 実績日を省いて送られた行（前の画面・％の入力だけの道）に、保存してある実績日を入れる（API の PUT）。
 * 進みが変わっていれば％ ON と同じ決まり（setProgressPct）で今日の日を入れる。送られた実績日（null も）はそのまま使う
 */
export function carryActuals<T extends ScheduleRow>(
  next: T,
  prev: { progress: number | null; actualStart: string | null; actualEnd: string | null } | undefined,
  today: string,
): T {
  if (next.kind !== "task" || (next.actualStart !== undefined && next.actualEnd !== undefined)) return next;
  const base = { ...next, actualStart: next.actualStart !== undefined ? next.actualStart : (prev?.actualStart ?? null), actualEnd: next.actualEnd !== undefined ? next.actualEnd : (prev?.actualEnd ?? null) };
  const p0 = prev?.progress ?? 0;
  const p1 = next.progress ?? 0;
  if (p0 === p1) return base;
  return setProgressPct({ ...base, progress: p0 }, p1, today);
}

/* ---------------- 足す位置・まとめてずらす ---------------- */

/** 行を足すときの着工日の既定：今ある最後の終わりの次の稼働日（行が無ければ今日か次の稼働日。docs/39 §4.1 の6） */
export function defaultStartAfter(rows: readonly ScheduleRow[], today: string, cal: WorkCalendar): string {
  let last: string | null = null;
  for (const r of rows) {
    if ((r.kind !== "task" && r.kind !== "milestone") || !r.start) continue;
    const e = r.end ?? r.start;
    if (!last || e > last) last = e;
  }
  return last ? nextWorkDay(last, cal) : snapToWorkDay(today, cal);
}
/** 工程表の下に行とつなぎを足してそろえる */
export function appendDoc(base: ScheduleDoc, add: ScheduleDoc, cal: WorkCalendar): ScheduleDoc {
  return normalizeSchedule({ rows: [...base.rows, ...add.rows], links: [...base.links, ...add.links] }, cal);
}
/**
 * 日付から後ろをまとめてずらす（docs/39 §4.1 の8）：その日から後ろに始まる工程・節目を ±n 稼働日。
 * その日より前に始まっている工程は動かさない（止まるなら中止を使う）。動かした行の数も返す
 */
export function shiftFrom(doc: ScheduleDoc, from: string, n: number, cal: WorkCalendar): { doc: ScheduleDoc; moved: number } {
  let moved = 0;
  const rows = doc.rows.map((r) => {
    if ((r.kind !== "task" && r.kind !== "milestone") || !r.start || r.start < from || !n) return r;
    moved++;
    const rc = calendarForRow(r, cal);
    const s = addWorkDays(snapToWorkDay(r.start, rc, n < 0 ? -1 : 1), n, rc);
    return r.kind === "milestone" ? { ...r, start: s, end: s } : { ...r, start: s };
  });
  return { doc: normalizeSchedule({ rows, links: doc.links }, cal), moved };
}
/** 終わりの日が変わった工程の数（中止にしたときの「n工程が延びました」） */
export function changedTaskCount(before: readonly ScheduleRow[], after: readonly ScheduleRow[]): number {
  const b = new Map(before.map((r) => [r.id, r]));
  return after.filter((r) => r.kind === "task" && r.end && b.get(r.id)?.end && b.get(r.id)!.end !== r.end).length;
}

/* ---------------- 中止 ---------------- */

/** 中止の対象（足すとき）：現場全体か、選んだ工程 */
export type StopTarget = { scope: "all" } | { scope: "rows"; rowIds: readonly string[] };
const byDate = (a: { date: string }, b: { date: string }) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
/**
 * 同じ日の中止を1つにまとめる（#105、docs/39 §4.8.1）：工程の和。どちらかが現場全体なら現場全体。
 * 理由・メモは先にあった方のまま（上書きしない）
 */
export function mergeStop(a: ScheduleStop, b: ScheduleStop): ScheduleStop {
  if (a.scope === "all" || b.scope === "all") return { ...a, scope: "all", rowIds: [] };
  return { ...a, scope: "rows", rowIds: [...new Set([...a.rowIds, ...b.rowIds])] };
}
/** 中止を足す（から〜まで）。もうある日は mergeStop でまとめる。target を省けば現場全体 */
export function addStops(stops: readonly ScheduleStop[], from: string, to: string, reason: StopReason, note: string | null, target0: StopTarget = { scope: "all" }): ScheduleStop[] {
  const out = new Map(stops.map((x) => [x.date, x]));
  // {siteShort}の休み（#293）は{siteShort}全体だけ
  const target: StopTarget = reason === "off" ? { scope: "all" } : target0;
  for (const date of datesBetween(from, to)) {
    const add: ScheduleStop = {
      date,
      reason,
      note: (reason === "other" || reason === "off") && note?.trim() ? note.trim() : null,
      scope: target.scope,
      rowIds: target.scope === "rows" ? [...new Set(target.rowIds)] : [],
    };
    const have = out.get(date);
    out.set(date, have ? mergeStop(have, add) : add);
  }
  return [...out.values()].sort(byDate);
}
/**
 * 中止をそろえる（画面は変えるたび・API は受け取った中止に）：工程でない行・無い行の id を外し、止める工程が0になった中止は消す。
 * 現場全体の中止は行を持たない。同じ日は1つに。日付の順
 */
export function tidyStops(stops: readonly ScheduleStop[], rows: readonly Pick<ScheduleRow, "id" | "kind">[]): ScheduleStop[] {
  const tasks = new Set(rows.filter((r) => r.kind === "task").map((r) => r.id));
  const out = new Map<string, ScheduleStop>();
  for (const x0 of stops) {
    const scope = x0.scope ?? "all";
    const x: ScheduleStop = { ...x0, scope, rowIds: scope === "rows" ? [...new Set((x0.rowIds ?? []).filter((id) => tasks.has(id)))] : [] };
    if (x.scope === "rows" && !x.rowIds.length) continue;
    const have = out.get(x.date);
    out.set(x.date, have ? mergeStop(have, x) : x);
  }
  return [...out.values()].sort(byDate);
}
/** その工程を中止から外す（dates の日の「工程を選んだ中止」だけ。止める工程が0になった中止は消える） */
export function removeRowFromStops(stops: readonly ScheduleStop[], rowId: string, dates: readonly string[]): ScheduleStop[] {
  const ds = new Set(dates);
  return stops.flatMap((x) => {
    if (!ds.has(x.date) || x.scope !== "rows" || !x.rowIds.includes(rowId)) return [x];
    const rowIds = x.rowIds.filter((id) => id !== rowId);
    return rowIds.length ? [{ ...x, rowIds }] : [];
  });
}
/** その工程を選んだ中止の日（現場全体の中止は入れない。日付の順） */
export function rowStopDates(stops: readonly ScheduleStop[], rowId: string): string[] {
  return stops.filter((x) => x.scope === "rows" && x.rowIds.includes(rowId)).map((x) => x.date).sort();
}
/** 日を続きの期間にまとめる（「10/1」「10/8〜10/9」） */
export function dateRuns(dates: readonly string[]): { from: string; to: string; dates: string[] }[] {
  const out: { from: string; to: string; dates: string[] }[] = [];
  for (const d of [...new Set(dates)].sort()) {
    const last = out[out.length - 1];
    if (last && dayNo(d) === dayNo(last.to) + 1) {
      last.to = d;
      last.dates.push(d);
    } else out.push({ from: d, to: d, dates: [d] });
  }
  return out;
}
/** 中止を続きの期間ごとにまとめる（一覧に出す。同じ理由・メモ・対象（現場全体か、同じ工程）で日が続くものを1つに） */
export function stopRuns(
  stops: readonly ScheduleStop[],
): { from: string; to: string; reason: StopReason; note: string | null; scope: StopScope; rowIds: string[]; dates: string[] }[] {
  const out: { from: string; to: string; reason: StopReason; note: string | null; scope: StopScope; rowIds: string[]; dates: string[] }[] = [];
  const keyOf = (x: { scope?: StopScope; rowIds?: readonly string[] }) => ((x.scope ?? "all") === "all" ? "all" : [...(x.rowIds ?? [])].sort().join(","));
  for (const x of [...stops].sort(byDate)) {
    const last = out[out.length - 1];
    if (last && dayNo(x.date) === dayNo(last.to) + 1 && last.reason === x.reason && last.note === x.note && keyOf(last) === keyOf(x)) {
      last.to = x.date;
      last.dates.push(x.date);
    } else out.push({ from: x.date, to: x.date, reason: x.reason, note: x.note, scope: x.scope ?? "all", rowIds: [...(x.rowIds ?? [])], dates: [x.date] });
  }
  return out;
}
/** トーストの日付「10/1」「10/8・10/9」「10/8〜10/12」 */
export function datesText(from: string, to: string): string {
  const n = dayNo(to) - dayNo(from) + 1;
  return n <= 1 ? mdOf(from) : n === 2 ? `${mdOf(from)}・${mdOf(to)}` : `${mdOf(from)}〜${mdOf(to)}`;
}
/**
 * 中止の候補（#105、docs/39 §4.8.1「この日にかかる工程」）：その期間（から〜まで）に稼働日がある工程（中止が無ければ作業する日で見る）と、
 * その期間にもう止めている工程（止めた日は工程の期間の外に押し出されていることがある）。
 * checked＝最初からチェックを付けるか（「雨でも作業する」工程は外す）。並びは工程表の上から
 */
export function stopCandidates(doc: ScheduleDoc, from: string, to: string, cal: WorkCalendar): { row: ScheduleRow; checked: boolean }[] {
  // 中止の無い暦（日ごとの休み・作業＝#293 は入れる）
  const plain: WorkCalendar = { offWeekdays: cal.offWeekdays, holidayOff: cal.holidayOff, holidays: cal.holidays, companyOff: cal.companyOff ?? NO_DAYS, stops: NO_DAYS, ...(cal.rowDays ? { rowDays: cal.rowDays } : {}) };
  const out: { row: ScheduleRow; checked: boolean }[] = [];
  for (const r of doc.rows) {
    if (r.kind !== "task" || !r.start || !r.end) continue;
    const a = r.start > from ? r.start : from;
    const b = r.end < to ? r.end : to;
    const rc = calendarForRow({ kind: r.kind, id: r.id, workOffDays: r.workOffDays }, plain);
    const stopped = [...(cal.rowStops?.get(r.id) ?? [])].some((d) => d >= from && d <= to);
    if (!stopped && (a > b || workDaysBetween(a, b, rc) === 0)) continue;
    out.push({ row: r, checked: !r.rainOk });
  }
  return out;
}
/**
 * 中止にするとずれる行（結果の1行「2工程を止める → 足場解体と「引き渡し」が2日ずれます」）：止めた工程のほかで、始まり・終わりが後ろへ動く工程と節目。
 * days＝稼働日で数えたずれ（動いた行のうち大きい方）。same＝全部同じずれか
 */
export function stopShifts(
  before: readonly ScheduleRow[],
  after: readonly ScheduleRow[],
  stopped: ReadonlySet<string>,
  cal: WorkCalendar,
): { rows: ScheduleRow[]; days: number; same: boolean } {
  const b = new Map(before.map((r) => [r.id, r]));
  const rows: ScheduleRow[] = [];
  const shifts: number[] = [];
  for (const r of after) {
    if (stopped.has(r.id) || (r.kind !== "task" && r.kind !== "milestone") || !r.start) continue;
    const o = b.get(r.id);
    const e0 = o?.end ?? o?.start;
    const e1 = r.end ?? r.start;
    if (!e0 || e1 <= e0) continue;
    rows.push(r);
    shifts.push(Math.max(1, workDaysBetween(addDays(e0, 1), e1, calendarForRow(r, cal))));
  }
  const days = shifts.length ? Math.max(...shifts) : 0;
  return { rows, days, same: shifts.every((n) => n === days) };
}
/** 「10/1」「10/1〜10/3」 */
export const spanText = (from: string, to: string): string => (from === to ? mdOf(from) : `${mdOf(from)}〜${mdOf(to)}`);

/* ---------------- 雛形（docs/39 §4.1 の13・14） ---------------- */

export const ScheduleTemplateRow = z.object({
  kind: ScheduleRowKind,
  name: z.string().max(SCHEDULE_LIMITS.memo).default(""),
  note: z.string().max(SCHEDULE_LIMITS.note).nullable().default(null),
  /** 最初の開始からの稼働日の数（日付未定・見出し・メモは null） */
  offset: z.number().int().min(0).max(36600).nullable().default(null),
  days: z.number().int().min(1).max(SCHEDULE_LIMITS.workDays).nullable().default(null),
  assignee: ScheduleAssignee.nullable().default(null),
  collapsed: z.boolean().default(false),
  workOffDays: z.boolean().default(false),
  /** 雨でも作業する（#105。schema_version はそのまま、無ければ false） */
  rainOk: z.boolean().default(false),
  /** 人数（#102。schema_version はそのまま、無ければ空） */
  crew: z.number().int().min(1).max(SCHEDULE_LIMITS.crewMax).nullable().default(null),
  /** 随時（#291。無ければ false） */
  intermittent: z.boolean().default(false),
});
export type ScheduleTemplateRow = z.infer<typeof ScheduleTemplateRow>;
/** 雛形の中身（schedule_templates.content）。つなぎは行の番号で持つ */
export const ScheduleTemplateContent = z.object({
  schema_version: z.literal(1),
  rows: z.array(ScheduleTemplateRow).max(SCHEDULE_LIMITS.rows),
  links: z.array(z.object({ from: z.number().int().min(0), to: z.number().int().min(0) })).max(SCHEDULE_LIMITS.links),
});
export type ScheduleTemplateContent = z.infer<typeof ScheduleTemplateContent>;

/** 日付を持つ行（工程・節目・手で期間を入れた見出し。#291） */
const DATED: readonly ScheduleRowKind[] = ["task", "milestone", "heading"];
/** 最初の開始（工程・節目・見出しの期間のいちばん早い日） */
function firstStartOf(rows: readonly ScheduleRow[]): string | null {
  let f: string | null = null;
  for (const r of rows) if (DATED.includes(r.kind) && r.start && (!f || r.start < f)) f = r.start;
  return f;
}
/**
 * 工程表 → 雛形：日付を「最初の開始からの稼働日の数」にして持つ（休みの形が違う工程表に置いても稼働日で合う）。
 * 金額・進み・中止・見積のつながり・覚えた遅れは持たない。「休日も」「雨でも」（#105）・人数（#102）は持つ
 */
export function toTemplate(doc: ScheduleDoc, cal: WorkCalendar): ScheduleTemplateContent {
  const base = firstStartOf(doc.rows);
  const index = new Map(doc.rows.map((r, i) => [r.id, i]));
  const rows: ScheduleTemplateRow[] = doc.rows.map((r) => ({
    kind: r.kind,
    name: r.name,
    note: r.kind === "heading" ? (r.note ?? "") : null,
    offset: base && r.start && DATED.includes(r.kind) ? workDaysBetween(base, addDays(r.start, -1), cal) : null,
    days: r.kind === "task" || (r.kind === "heading" && r.start) ? r.days : null,
    assignee: r.kind !== "memo" ? r.assignee : null,
    collapsed: r.kind === "heading" ? r.collapsed : false,
    workOffDays: r.kind === "task" ? !!r.workOffDays : false,
    rainOk: r.kind === "task" ? !!r.rainOk : false,
    crew: r.kind === "task" ? (r.crew ?? null) : null,
    intermittent: r.kind === "task" ? !!r.intermittent : false,
  }));
  const links = doc.links.flatMap((l) => {
    const a = index.get(l.from);
    const b = index.get(l.to);
    return a == null || b == null ? [] : [{ from: a, to: b }];
  });
  return { schema_version: 1, rows, links };
}
/** 雛形 → 行とつなぎ：着工日（休みなら次の稼働日）から稼働日の数で置き直してそろえる。id は newId で新しく */
export function fromTemplate(tpl: ScheduleTemplateContent, start: string, cal: WorkCalendar, newId: () => string): ScheduleDoc {
  const base = snapToWorkDay(start, cal);
  const ids = tpl.rows.map(() => newId());
  const rows: ScheduleRow[] = tpl.rows.map((t, i) => {
    const r = newRow(t.kind, ids[i]!, null);
    // 見出しは期間（offset と days）があるときだけ日付を持つ（#291）
    const dated = t.offset != null && (t.kind === "task" || t.kind === "milestone" || (t.kind === "heading" && t.days != null));
    const s = dated ? addWorkDays(base, t.offset!, cal) : null;
    return {
      ...r,
      name: t.name,
      note: t.kind === "heading" ? (t.note ?? "") : null,
      start: s,
      end: t.kind === "milestone" ? s : null,
      days: (t.kind === "task" || t.kind === "heading") && s ? (t.days ?? 1) : null,
      assignee: t.kind !== "memo" ? t.assignee : null,
      collapsed: t.kind === "heading" ? t.collapsed : false,
      workOffDays: t.kind === "task" ? t.workOffDays : false,
      rainOk: t.kind === "task" ? !!t.rainOk : false,
      crew: t.kind === "task" ? (t.crew ?? null) : null,
      intermittent: t.kind === "task" ? !!t.intermittent : false,
    };
  });
  const links = tpl.links.flatMap((l) => (ids[l.from] && ids[l.to] && l.from !== l.to ? [{ from: ids[l.from]!, to: ids[l.to]! }] : []));
  return normalizeSchedule({ rows, links }, cal);
}

/* ---------------- 見積から作る（docs/39 §4.1 の1〜6、Figma B） ---------------- */

/** 見積の大項目（GET /groups/:id/schedule/estimate-items。金額は DB の fn_schedule_estimate_items が出す） */
export const ScheduleEstimateItem = z.object({
  key: z.string(),
  kind: z.enum(["sheet", "block", "line"]),
  name: z.string(),
  /** 税抜・円（値引きはマイナス） */
  amount: z.number().int(),
  /** 最初からチェックが付いているか（率の行・値引き・別途は false） */
  defaultOn: z.boolean(),
  /** 日数の案（人工・人日・日の数量の合計を切り上げ。無ければ 1） */
  suggestedDays: z.number().int(),
  /** 業者の案（仕入れが全部同じ外注先の見積なら）。name は略称、fullName は正式名（#102 §5.1 の1） */
  vendor: z.object({ id: z.string().uuid(), name: z.string(), fullName: z.string().default("") }).nullable(),
});
export type ScheduleEstimateItem = z.infer<typeof ScheduleEstimateItem>;
export const ScheduleEstimateItems = z.object({
  /** この案件の見積（受注 → 出した → 下書きの順） */
  estimates: z.array(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      no: z.string().nullable(),
      status: z.enum(["draft", "waiting", "ordered", "billed"]),
      totalExTax: z.number().int(),
    }),
  ),
  /** 選んでいる見積（無ければ null） */
  estimateId: z.string().uuid().nullable(),
  items: z.array(ScheduleEstimateItem),
});
export type ScheduleEstimateItems = z.infer<typeof ScheduleEstimateItems>;

/** POST /groups/:id/schedule/from-estimate（チェックした項目だけ。金額は送らない＝API が DB から引き直す） */
export const ScheduleFromEstimate = z.object({
  baseVersion: z.number().int(),
  estimateId: z.string().uuid(),
  start: Ymd,
  chain: z.boolean().default(true),
  /** 見出しを付けて下に足す（その名前）。付けないなら null */
  heading: z.string().trim().max(SCHEDULE_LIMITS.name).nullable().default(null),
  items: z
    .array(z.object({ key: z.string().max(80), days: z.number().int().min(1).max(SCHEDULE_LIMITS.workDays), assignee: ScheduleAssignee.nullable().default(null) }))
    .min(1, SCHEDULE_MSG.noItemPicked)
    .max(SCHEDULE_LIMITS.rows, SCHEDULE_MSG.tooManyRows),
});
export type ScheduleFromEstimate = z.input<typeof ScheduleFromEstimate>;
export const ScheduleFromTemplate = z.object({ baseVersion: z.number().int(), templateId: z.string().uuid(), start: Ymd });
export type ScheduleFromTemplate = z.infer<typeof ScheduleFromTemplate>;
export const ScheduleFromGroup = z.object({ baseVersion: z.number().int(), sourceGroupId: z.string().uuid(), start: Ymd });
export type ScheduleFromGroup = z.infer<typeof ScheduleFromGroup>;

/** 見積の項目から 工程の行とつなぎ（金額は受け取った値のまま。マイナス＝値引きの項目は金額を持たない） */
export type EstimatePick = { key: string; name: string; amount: number | null; days: number; assignee: ScheduleAssignee | null };
export function rowsFromItems(
  items: readonly EstimatePick[],
  opts: { start: string; chain: boolean; heading: string | null; sourceDocumentId: string | null },
  cal: WorkCalendar,
  newId: () => string,
): ScheduleDoc {
  const rows: ScheduleRow[] = [];
  if (opts.heading != null) rows.push({ ...newRow("heading", newId(), null), name: opts.heading, note: "" });
  const tasks: ScheduleRow[] = items.map((it) => ({
    ...newRow("task", newId(), opts.start),
    name: it.name.slice(0, SCHEDULE_LIMITS.name),
    days: Math.max(1, Math.min(SCHEDULE_LIMITS.workDays, Math.round(it.days))),
    assignee: it.assignee,
    amount: it.amount != null && it.amount >= 0 ? it.amount : null,
    sourceDocumentId: opts.sourceDocumentId,
    sourceRef: it.key,
  }));
  rows.push(...tasks);
  const links = opts.chain ? tasks.slice(1).map((t, i) => ({ from: tasks[i]!.id, to: t.id })) : [];
  return normalizeSchedule({ rows, links }, cal);
}

/* ---------------- ほかの API の形 ---------------- */

/** GET /schedule/sources：工程表のある案件（前の案件からうつす） */
export const ScheduleSource = z.object({
  groupId: z.string().uuid(),
  name: z.string(),
  clientName: z.string(),
  start: z.string().nullable(),
  end: z.string().nullable(),
  rowCount: z.number().int(),
});
export type ScheduleSource = z.infer<typeof ScheduleSource>;

/** 工程表の雛形（一覧・保存・名前） */
export const ScheduleTemplateItem = z.object({
  id: z.string().uuid(),
  name: z.string(),
  rowCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  /** 製品の標準テンプレート（#291。変更・削除はできない。「複製」で会社のテンプレートに） */
  builtin: z.boolean().default(false),
  /** 会社が非表示にした標準テンプレート（空の{schedule}の「テンプレートから」に出さない） */
  hidden: z.boolean().default(false),
});
export type ScheduleTemplateItem = z.infer<typeof ScheduleTemplateItem>;
/** テンプレートとして保存（#291 から案件なしの{schedule}は scheduleId で） */
export const ScheduleTemplateCreate = z
  .object({ name: z.string().trim().min(1, "名前を入力してください").max(SCHEDULE_LIMITS.templateName), groupId: z.string().uuid().optional(), scheduleId: z.string().uuid().optional() })
  .refine((x) => !!x.groupId || !!x.scheduleId, { message: "{schedule}が見つかりません", path: ["groupId"] });
export type ScheduleTemplateCreate = z.infer<typeof ScheduleTemplateCreate>;
/** 標準テンプレートの非表示・表示（PUT /schedule-templates/:id/hidden。#291） */
export const ScheduleTemplateHidden = z.object({ hidden: z.boolean() });
export type ScheduleTemplateHidden = z.infer<typeof ScheduleTemplateHidden>;
export const ScheduleTemplateRename = z.object({ name: z.string().trim().min(1, "名前を入力してください").max(SCHEDULE_LIMITS.templateName) });
export type ScheduleTemplateRename = z.infer<typeof ScheduleTemplateRename>;

/** 設定 → 工程表の休み（GET/PUT /company/schedule-calendar）。曜日・祝日は新しい工程表の既定、会社の休みは全部の工程表に効く */
export const CompanyScheduleCalendar = z.object({
  offWeekdays: OffWeekdays,
  holidayOff: z.boolean(),
  offDays: z.array(CompanyOffDay).max(SCHEDULE_LIMITS.companyOffDays, SCHEDULE_MSG.offDaysTooMany),
  /** 進捗を％で入力（#292。答えにはいつも入る。PUT の休みの保存では使わない＝変えるのは PUT /company/schedule-progress） */
  progressPct: z.boolean().optional(),
});
export type CompanyScheduleCalendar = z.infer<typeof CompanyScheduleCalendar>;
/** PUT /company/schedule-progress（設定 → {schedule} の「進捗を％で入力」。管理者。押した瞬間に保存。#292） */
export const CompanyScheduleProgress = z.object({ progressPct: z.boolean() });
export type CompanyScheduleProgress = z.infer<typeof CompanyScheduleProgress>;

/* ============================================================================
 * 3回目（#102、docs/39 §5）：全体の工程表・重なり・その日の人数・段分け・「から始める」
 * ========================================================================== */

/**
 * GET /schedule/overview の行（その期間にかかる工程と節目。担当の業者・{顧客}は略称）。
 * 10/1：ahead=1 で期間の後ろの工程・節目も（後ろにしか無い工程表も）、late=1 で期間より前に終わるはずの遅れている工程も
 */
export const ScheduleOverviewRow = z.object({
  id: z.string().uuid(),
  kind: z.enum(["task", "milestone"]),
  name: z.string(),
  start: z.string(),
  days: z.number().int().nullable(),
  end: z.string().nullable(),
  progress: z.number().int().nullable(),
  assignee: ScheduleAssignee.nullable(),
  workOffDays: z.boolean(),
  crew: z.number().int().nullable(),
  /** 工程表の中の並び（工程表のまま並べる） */
  position: z.number().int(),
  /** 知らせる遅れの日数（shownDelayOf。工程だけ・無ければ 0。10/1） */
  late: z.number().int().optional(),
  /** 実績の着手日・完了日（#292。状況＝taskStateOf に使う。#292 より前の答えには無い） */
  actualStart: z.string().nullable().optional(),
  actualEnd: z.string().nullable().optional(),
});
export type ScheduleOverviewRow = z.infer<typeof ScheduleOverviewRow>;
/** 全体の工程表の業者（#102 の ScheduleAssigneePartner ＋ リンクの期限。止めていないリンクが無ければ null） */
export const ScheduleOverviewPartner = ScheduleAssigneePartner.extend({ linkExpiresAt: z.string().nullable().default(null) });
export type ScheduleOverviewPartner = z.infer<typeof ScheduleOverviewPartner>;

/** 全体の工程表の1現場（工程表のある{案件}） */
export const ScheduleOverviewItem = z.object({
  /** {schedule}の id（#291。現場の目印＝案件なしの{schedule}も1つずつ） */
  scheduleId: z.string().uuid(),
  /** 案件（案件なしの{schedule}は null。#291） */
  groupId: z.string().uuid().nullable(),
  /** {project}名（案件なしは題） */
  groupName: z.string(),
  /** {顧客}（略称） */
  clientName: z.string(),
  staffName: z.string(),
  /** 暦（その工程表の休み・会社の休みを使うか・中止） */
  offWeekdays: z.array(z.number().int()),
  holidayOff: z.boolean(),
  useCompanyOffDays: z.boolean(),
  stops: z.array(ScheduleStop),
  /** {task}の日ごとの休み・作業（#293。この{schedule}の分。#293 より前の答えには無い） */
  rowDays: z.array(ScheduleRowDay).optional(),
  /** {siteShort}の作業日（#296。この{schedule}の分。#296 より前の答えには無い） */
  workDays: z.array(z.string()).optional(),
  /** 工程表全体の 始まり・終わり・進み（期間の外の行も入れて） */
  start: z.string().nullable(),
  end: z.string().nullable(),
  progress: z.number().int().nullable(),
  /** 完了した{task}の数と{task}の数（#292。％ OFF の「完了 n／m{task}」。#292 より前の答えには無い） */
  doneCount: z.number().int().optional(),
  taskCount: z.number().int().optional(),
  /** 工程の遅れの一番大きい日数（知らせる遅れ。無ければ 0） */
  lateDays: z.number().int(),
  rows: z.array(ScheduleOverviewRow),
});
export type ScheduleOverviewItem = z.infer<typeof ScheduleOverviewItem>;
/** GET /schedule/overview?from=&to=（期間は 0〜400日。工期の始まりの順に 300 まで） */
export const ScheduleOverview = z.object({
  from: z.string(),
  to: z.string(),
  /** 会社の休み（設定「工程表の休み」：曜日・祝日＝地の休み、会社の休み） */
  company: CompanyScheduleCalendar,
  holidays: z.array(Holiday),
  /** 担当の業者・{顧客}（略称・正式名・フリガナ。業者ごとの並び・探すに使う）。#103：止めていないリンクの期限 */
  partners: z.array(ScheduleOverviewPartner),
  schedules: z.array(ScheduleOverviewItem),
  /** 300 を超えた（工期の始まりの順に 300 まで返した） */
  truncated: z.boolean(),
});
export type ScheduleOverview = z.infer<typeof ScheduleOverview>;

/** 全体の工程表の1現場の暦（その工程表の休み・祝日・会社の休み（使うなら）・中止） */
export function overviewCalendar(
  item: Pick<ScheduleOverviewItem, "offWeekdays" | "holidayOff" | "useCompanyOffDays" | "stops"> & { rowDays?: readonly RowDayInput[] | undefined; workDays?: readonly string[] | undefined },
  holidays: Iterable<string | { date: string }>,
  companyOffDays: readonly { from: string; to: string }[],
): WorkCalendar {
  return workCalendar(item, holidays, { companyOffDays, stops: item.stops, rowDays: item.rowDays ?? [], workDays: item.workDays ?? [] });
}

/**
 * その工程の稼働日の一覧（docs/39 §5.5）：開始〜終わりのうち、その行の暦（calendarForRow＝「休日も」・工程を選んだ中止）で休みでない日。
 * 節目はその日。日付の無い行・見出し・メモは空。from・to で切り取れる（全体の工程表の期間）
 */
export function workDatesOf(
  r: { kind: ScheduleRowKind; start: string | null; end: string | null; id?: string | undefined; workOffDays?: boolean | undefined },
  cal: WorkCalendar,
  clip?: { from?: string | undefined; to?: string | undefined },
): string[] {
  if (!r.start) return [];
  if (r.kind === "milestone") return (!clip?.from || r.start >= clip.from) && (!clip?.to || r.start <= clip.to) ? [r.start] : [];
  if (r.kind !== "task") return [];
  const a = clip?.from && clip.from > r.start ? clip.from : r.start;
  const e = r.end ?? r.start;
  const b = clip?.to && clip.to < e ? clip.to : e;
  if (a > b) return [];
  const rc = calendarForRow({ kind: r.kind, id: r.id, workOffDays: r.workOffDays }, cal);
  const out: string[] = [];
  for (let d = dayNo(a); d <= dayNo(b); d++) {
    const ymd = ymdOfDayNo(d);
    if (!isOffDay(ymd, rc)) out.push(ymd);
  }
  return out;
}

/**
 * 重なりを見る担当の鍵（docs/39 §5.2 の4）：業者は 'p:id'、自社の人は 'u:id'。書いた担当（「ガス会社」など）と担当なしは見ない（null）。
 * 同じ文字でも別の人のことがあり、「自社」などで誤りが多くなるため
 */
export function assigneeKeyOf(a: ScheduleAssignee | null | undefined): string | null {
  if (!a) return null;
  if (a.type === "partner") return `p:${a.id}`;
  if (a.type === "user") return `u:${a.id}`;
  return null;
}
/** 重なりを数える工程か：工程で・進みが 100% 未満・担当が業者か自社の人・日付がある */
export function overlapCandidate(r: { kind: string; progress: number | null; assignee: ScheduleAssignee | null; start: string | null }): boolean {
  return r.kind === "task" && (r.progress ?? 0) < 100 && !!r.start && assigneeKeyOf(r.assignee) != null;
}

/** 重なりを数えるもの：どの現場（siteId＝{schedule}の id。#291 で案件なしの{schedule}も1現場）の・どの工程（rowId）に・だれが（key）・どの日に（dates＝稼働日） */
export type OverlapItem = { siteId: string; rowId: string; key: string; dates: readonly string[] };
export type OverlapResult = {
  /** 重なりの件＝重なった工程の組の数（同じ業者の A 現場の工程と B 現場の工程で1件。3現場が同じ日なら3件） */
  count: number;
  /** 担当ごと：days＝2現場以上に入っている日（日付の順）・pairs＝件・groups＝入っている現場の数（期間の中） */
  byKey: Map<string, { days: string[]; pairs: number; groups: number }>;
  /** 工程ごと：dates＝重なった日・others＝相手の工程（現場・工程・重なった日） */
  byRow: Map<string, { dates: string[]; others: { siteId: string; rowId: string; dates: string[] }[] }>;
};
/**
 * 重なり（docs/39 §5.2 の4・5、§5.5 overlapsOf）：同じ担当（業者か自社の人）が、同じ日に2つ以上の現場の工程に入っていること。
 * 同じ現場の2工程は重なりにしない。日は それぞれの工程表の稼働日（呼ぶ側が workDatesOf で渡す）で比べる
 */
export function overlapsOf(items: readonly OverlapItem[]): OverlapResult {
  const byKeyItems = new Map<string, OverlapItem[]>();
  for (const it of items) {
    if (!it.dates.length) continue;
    byKeyItems.set(it.key, [...(byKeyItems.get(it.key) ?? []), it]);
  }
  const byKey: OverlapResult["byKey"] = new Map();
  const byRow: OverlapResult["byRow"] = new Map();
  let count = 0;
  const addRow = (a: OverlapItem, b: OverlapItem, dates: string[]) => {
    const cur = byRow.get(a.rowId) ?? { dates: [], others: [] };
    cur.dates = [...new Set([...cur.dates, ...dates])].sort();
    cur.others.push({ siteId: b.siteId, rowId: b.rowId, dates });
    byRow.set(a.rowId, cur);
  };
  for (const [key, list] of byKeyItems) {
    const sets = list.map((x) => new Set(x.dates));
    const groupsOn = new Map<string, Set<string>>();
    for (const x of list) for (const d of x.dates) groupsOn.set(d, (groupsOn.get(d) ?? new Set()).add(x.siteId));
    let pairs = 0;
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]!;
        const b = list[j]!;
        if (a.siteId === b.siteId) continue;
        const shared = a.dates.filter((d) => sets[j]!.has(d));
        if (!shared.length) continue;
        pairs++;
        addRow(a, b, shared);
        addRow(b, a, shared);
      }
    }
    const days = [...groupsOn].filter(([, g]) => g.size >= 2).map(([d]) => d).sort();
    byKey.set(key, { days, pairs, groups: new Set(list.map((x) => x.siteId)).size });
    count += pairs;
  }
  return { count, byKey, byRow };
}

/** その日の人数（docs/39 §5.2 の7）：日ごとに、その日に稼働する工程の人数の合計。人数の無い工程は数えない */
export function crewByDate(items: readonly { dates: readonly string[]; crew: number | null | undefined }[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const it of items) {
    if (!it.crew) continue;
    for (const d of it.dates) out.set(d, (out.get(d) ?? 0) + it.crew);
  }
  return out;
}

/** 案件の工程表の重なりを数える相手（ほかの工程表の工程。dates はその工程表の暦で数えた稼働日） */
export type ConflictOther = ScheduleConflictOther & { key: string; dates: readonly string[] };
/**
 * 案件の工程表の重なり（docs/39 §5.5 conflictsFor）：この工程表の工程（進み 100% 未満・担当が業者か自社の人）ごとに、
 * 同じ担当の ほかの工程表の工程と稼働日が重なるもの。並びは工程表の上から、相手は始まりの順
 */
export function conflictsFor(doc: ScheduleDoc, cal: WorkCalendar, others: readonly ConflictOther[]): ScheduleConflict[] {
  const out: ScheduleConflict[] = [];
  for (const r of doc.rows) {
    if (!overlapCandidate(r)) continue;
    const key = assigneeKeyOf(r.assignee)!;
    const mine = new Set(workDatesOf(r, cal));
    if (!mine.size) continue;
    const days = new Set<string>();
    const hit: ScheduleConflictOther[] = [];
    for (const o of others) {
      if (o.key !== key) continue;
      const shared = o.dates.filter((d) => mine.has(d));
      if (!shared.length) continue;
      for (const d of shared) days.add(d);
      hit.push({ scheduleId: o.scheduleId, groupId: o.groupId, groupName: o.groupName, rowId: o.rowId, rowName: o.rowName, start: o.start, end: o.end, crew: o.crew });
    }
    if (!hit.length) continue;
    hit.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : a.groupName.localeCompare(b.groupName, "ja")));
    out.push({ rowId: r.id, days: [...days].sort(), others: hit });
  }
  return out;
}

/**
 * 段分け（業者ごとの行。docs/39 §5.5 lanesOf）：重なるバーは段を分け、空いている一番上の段へ。重ならないバーは同じ段に入る。
 * 並びは始まりの順（同じなら終わりの順）。count＝段の数
 */
export function lanesOf(bars: readonly { id: string; start: string; end: string }[]): { lane: Map<string, number>; count: number } {
  const sorted = [...bars].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : a.end < b.end ? -1 : a.end > b.end ? 1 : 0));
  const lastEnd: string[] = [];
  const lane = new Map<string, number>();
  for (const b of sorted) {
    let k = lastEnd.findIndex((e) => e < b.start);
    if (k < 0) k = lastEnd.length;
    lastEnd[k] = b.end;
    lane.set(b.id, k);
  }
  return { lane, count: lastEnd.length };
}

/** 「10/17(土) から始める」の日：相手のいちばん遅い終わりの次の稼働日（この工程の暦） */
export function conflictStartDate(c: Pick<ScheduleConflict, "others">, r: Pick<ScheduleRow, "kind" | "id" | "workOffDays">, cal: WorkCalendar): string | null {
  let last: string | null = null;
  for (const o of c.others) if (!last || o.end > last) last = o.end;
  return last ? nextWorkDay(last, calendarForRow(r, cal)) : null;
}
/**
 * 「10/17(土) から始める」（docs/39 §5.5 startAfter）：その工程をその日（稼働日でなければ次の稼働日）から始め、
 * つながった後ろも同じ稼働日の数だけ動かす（shiftWithSuccessors。ドラッグと同じく元に戻せる）
 */
export function startAfter(doc: ScheduleDoc, rowId: string, date: string, cal: WorkCalendar): ScheduleDoc {
  const r = doc.rows.find((x) => x.id === rowId);
  if (!r || !r.start || (r.kind !== "task" && r.kind !== "milestone")) return doc;
  const rc = calendarForRow(r, cal);
  const target = snapToWorkDay(date, rc);
  const from = snapToWorkDay(r.start, rc);
  if (target === from) return normalizeSchedule(doc, cal);
  const n = target > from ? workDaysBetween(addDays(from, 1), target, rc) : -workDaysBetween(target, addDays(from, -1), rc);
  return shiftWithSuccessors(doc, rowId, n, cal);
}

/* ============================================================================
 * 5回目（#104、docs/39 §7）：出力（紙・Excel・CSV）と画面が同じものを使う部品
 *   バーの区切り（workRuns）・全体の工程表の材料（overviewRowsOf）・現場ごとの絞り（overviewSitesOf）・業者ごと（overviewPeopleOf）
 * ========================================================================== */

/** 開始〜終わりのうち、稼働日の続き（塊）。休みの日は塊の間の細い線になる（画面のバーと紙のバーが同じ区切りになる） */
export function workRuns(start: string, end: string, cal: WorkCalendar): { from: string; to: string }[] {
  const out: { from: string; to: string }[] = [];
  let cur: { from: string; to: string } | null = null;
  for (let d = dayNo(start); d <= dayNo(end); d++) {
    const ymd = ymdOfDayNo(d);
    if (isOffDay(ymd, cal)) {
      if (cur) out.push(cur);
      cur = null;
    } else if (cur) cur.to = ymd;
    else cur = { from: ymd, to: ymd };
  }
  if (cur) out.push(cur);
  return out;
}

/** 全体の工程表の1行の材料（どの現場の・どの行・その暦・期間の中の稼働日） */
export type OverviewRowInfo = { item: ScheduleOverviewItem; row: ScheduleOverviewRow; cal: WorkCalendar; dates: string[] };
/** 全体の工程表の材料：会社の暦（地の休み）・行ごとの暦と稼働日・重なり（画面と紙が同じもの） */
export function overviewRowsOf(ov: ScheduleOverview): { companyCal: WorkCalendar; rows: OverviewRowInfo[]; overlaps: OverlapResult } {
  const companyCal = workCalendar({ offWeekdays: ov.company.offWeekdays, holidayOff: ov.company.holidayOff, useCompanyOffDays: true }, ov.holidays, { companyOffDays: ov.company.offDays });
  const rows: OverviewRowInfo[] = [];
  for (const item of ov.schedules) {
    const cal = overviewCalendar(item, ov.holidays, ov.company.offDays);
    for (const row of item.rows) rows.push({ item, row, cal, dates: workDatesOf(row, cal, { from: ov.from, to: ov.to }) });
  }
  const overlaps = overlapsOf(
    rows.filter((x) => overlapCandidate(x.row)).map((x) => ({ siteId: x.item.scheduleId, rowId: x.row.id, key: assigneeKeyOf(x.row.assignee)!, dates: x.dates })),
  );
  return { companyCal, rows, overlaps };
}

type PartnerLite = { name: string; fullName: string; kana: string };
/** 全体の工程表の行の担当の名前（業者・{顧客}は略称） */
export function overviewAssigneeName(partnerOf: ReadonlyMap<string, PartnerLite>, r: Pick<ScheduleOverviewRow, "assignee">): string {
  const a = r.assignee;
  if (!a) return "";
  if (a.type === "text") return a.text;
  if (a.type === "partner") return partnerOf.get(a.id)?.name ?? a.name ?? "";
  return a.name ?? "";
}
/** 探す：部分一致（大文字・小文字は同じ） */
const hitQ = (q: string, ...xs: (string | undefined | null)[]) => !q || xs.some((x) => !!x && x.toLowerCase().includes(q));
const normQ = (q: string) => q.trim().toLowerCase();
const rowHitQ = (partnerOf: ReadonlyMap<string, PartnerLite>, q: string, r: Pick<ScheduleOverviewRow, "assignee">) => {
  const a = r.assignee;
  if (!a) return false;
  if (a.type === "partner") {
    const p = partnerOf.get(a.id);
    return hitQ(q, p?.name, p?.fullName, p?.kana, a.name);
  }
  return hitQ(q, a.type === "text" ? a.text : a.name);
};
/** 現場ごと：探す（現場名・{顧客}名・工程の担当）で絞った現場 */
export function overviewSitesOf(ov: ScheduleOverview, q0: string): ScheduleOverviewItem[] {
  const q = normQ(q0);
  const partnerOf = new Map(ov.partners.map((p) => [p.id, p]));
  return ov.schedules.filter((it) => hitQ(q, it.groupName, it.clientName) || it.rows.some((r) => rowHitQ(partnerOf, q, r)));
}

/** 業者ごとの1本のバー（その人の工程） */
export type OverviewPersonBar = {
  id: string;
  /** {schedule}の id（#291。現場の目印） */
  scheduleId: string;
  groupId: string | null;
  groupName: string;
  clientName: string;
  name: string;
  start: string;
  end: string;
  crew: number | null;
  progress: number;
  /** 実績の着手日（#292。状況＝taskStateOf） */
  actualStart: string | null;
  workOffDays: boolean;
  /** この工程の重なった日 */
  overlap: string[];
  /** 知らせる遅れの日数（無ければ 0。10/1） */
  late: number;
};
/** 業者ごとの1行：p＝業者（取引先）・u＝自社の人・t＝書いた担当・n＝担当なし */
export type OverviewPerson = { key: string; kind: "p" | "u" | "t" | "n"; name: string; full: string; sort: string; bars: OverviewPersonBar[]; groups: number; overlapDays: string[] };
/**
 * 業者ごと（docs/39 §5.3.1）：工程を担当ごとにまとめる。探すは 人（略称・正式名・フリガナ）に当たれば全部、当たらなければ 現場名・{顧客}名で当たる工程だけ。
 * 並びは 業者 → 自社 → 書いた担当 → 担当なし、その中はフリガナ（無ければ名前）の順。バーは始まりの順
 */
export function overviewPeopleOf(ov: ScheduleOverview, rows: readonly OverviewRowInfo[], overlaps: OverlapResult, q0: string): OverviewPerson[] {
  const q = normQ(q0);
  const partnerOf = new Map(ov.partners.map((p) => [p.id, p]));
  const map = new Map<string, OverviewPerson>();
  for (const info of rows) {
    const r = info.row;
    if (r.kind !== "task") continue;
    const a = r.assignee;
    const key = a ? (a.type === "text" ? `t:${a.text}` : assigneeKeyOf(a)!) : "n:";
    const kind: OverviewPerson["kind"] = !a ? "n" : a.type === "partner" ? "p" : a.type === "user" ? "u" : "t";
    let p = map.get(key);
    if (!p) {
      const pp = a?.type === "partner" ? partnerOf.get(a.id) : undefined;
      const name = kind === "n" ? "担当なし" : overviewAssigneeName(partnerOf, r);
      p = { key, kind, name, full: pp?.fullName ?? "", sort: pp?.kana || name, bars: [], groups: 0, overlapDays: [] };
      map.set(key, p);
    }
    p.bars.push({
      id: r.id,
      scheduleId: info.item.scheduleId,
      groupId: info.item.groupId,
      groupName: info.item.groupName,
      clientName: info.item.clientName,
      name: r.name,
      start: r.start,
      end: r.end ?? r.start,
      crew: r.crew,
      progress: r.progress ?? 0,
      actualStart: r.actualStart ?? null,
      workOffDays: r.workOffDays,
      overlap: overlaps.byRow.get(r.id)?.dates ?? [],
      late: r.late ?? 0,
    });
  }
  const out: OverviewPerson[] = [];
  for (const p of map.values()) {
    const pp = p.kind === "p" ? partnerOf.get(p.key.slice(2)) : undefined;
    const personHit = hitQ(q, p.name, p.full, pp?.kana);
    const bars = personHit ? p.bars : p.bars.filter((b) => hitQ(q, b.groupName, b.clientName));
    if (!bars.length) continue;
    bars.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
    out.push({ ...p, bars, groups: new Set(bars.map((b) => b.scheduleId)).size, overlapDays: p.kind === "p" || p.kind === "u" ? (overlaps.byKey.get(p.key)?.days ?? []) : [] });
  }
  const order = { p: 0, u: 1, t: 2, n: 3 } as const;
  return out.sort((a, b) => order[a.kind] - order[b.kind] || a.sort.localeCompare(b.sort, "ja"));
}

/* ============================================================================
 * 10/1 射場さん：期間の外の工程（全体の工程表の端の印・スマホの「この先の工程」。docs/39 実装の記録）
 * ========================================================================== */

/** 端の印の相手（その行のバー） */
export type EdgeBar = { id: string; start: string; end: string };
/**
 * 端の印（全体の工程表）：見えている日（left〜right）の外にあるバー。
 * before＝左に隠れたバーのうち いちばん近い（終わりが遅い）もの、after＝右に隠れたバーのうち いちばん近い（始まりが早い）もの。
 * 見えている日にかかるバーは印にしない（同じ行に見えているバーがあっても、外のバーは印にする）
 */
export function edgeMarksOf<T extends EdgeBar>(bars: readonly T[], left: string, right: string): { before: T | null; after: T | null } {
  let before: T | null = null;
  let after: T | null = null;
  for (const b of bars) {
    if (b.end < left) {
      if (!before || b.end > before.end) before = b;
    } else if (b.start > right) {
      if (!after || b.start < after.start) after = b;
    }
  }
  return { before, after };
}

/** スマホの「この先の工程」：その日の翌日から n日のうちに始まる工程。始まりの順、同じ日は現場の並び（工期の始まりの順）・工程表の並び */
export function upcomingOf(rows: readonly OverviewRowInfo[], day: string, n = 7): OverviewRowInfo[] {
  const last = addDays(day, n);
  const site = new Map<string, number>();
  for (const r of rows) if (!site.has(r.item.scheduleId)) site.set(r.item.scheduleId, site.size);
  return rows
    .filter((r) => r.row.kind === "task" && r.row.start > day && r.row.start <= last)
    .sort((a, b) => (a.row.start < b.row.start ? -1 : a.row.start > b.row.start ? 1 : site.get(a.item.scheduleId)! - site.get(b.item.scheduleId)! || a.row.position - b.row.position));
}
